import React,{useState,useCallback,useMemo,useEffect} from 'react';
import {T,GS,HABITS,DEFAULT_HABITS,getActiveHabits,ACHIEVEMENTS,GOAL_CATS,REWARD_TIERS,JOURNAL_PROMPTS,WEALTH_ACTIONS,LEVELS,TABS,FREE_FOREVER,INVESTABLE_TYPES,ASSET_TYPE_LABELS,ASSET_GROWTH_RATES,PLAN_CHECKLIST,
  fmt,daysUntil,getLvl,todayIdx,fmtDate,getWkStart,todayStr,monthsElapsed,daysSince,
  makeDefault,calcCF,calcNW,calcScore,getFWA,calcWealthAdvice,
  Card,Slab,Row,PBar,Btn,CheckBtn,Pill,NUM,Grid2,Grid4,Spinner,StatCard} from './core';
import {supabase,WelcomeScreen,AuthScreen,Onboarding,Paywall} from './auth';

// ── GUIDED UX COMPONENTS ──────────────────────────────────────────────────────

// Best-in-class date picker — shows formatted date, native picker on click
function DatePicker({value,onChange,placeholder='Select date',style={}}){
  const fmt=v=>{if(!v)return'';const d=new Date(v+'T00:00:00');return d.toLocaleDateString('en-AU',{day:'numeric',month:'short',year:'numeric'});};
  return(
    <div style={{position:'relative',...style}}>
      <input
        type="date"
        value={value||''}
        onChange={e=>onChange(e.target.value)}
        style={{
          position:'absolute',top:0,left:0,width:'100%',height:'100%',
          opacity:0,cursor:'pointer',zIndex:2,
        }}
      />
      <div className="cmd-input" style={{
        padding:'9px 12px',fontSize:13,color:value?T.textPrimary:T.textMuted,
        display:'flex',justifyContent:'space-between',alignItems:'center',
        background:T.bgPanel2,pointerEvents:'none',
      }}>
        <span>{value?fmt(value):placeholder}</span>
        <span style={{fontSize:14,color:T.textMuted}}>📅</span>
      </div>
    </div>
  );
}


// Contextual tip card — dismissible, stores dismissed state in localStorage
function ContextTip({id,icon,title,body,action,onAction,color}){
  const key=`cmd_tip_${id}`;
  const [visible,setVisible]=useState(()=>localStorage.getItem(key)!=='1');
  if(!visible)return null;
  const dismiss=()=>{localStorage.setItem(key,'1');setVisible(false);};
  const c=color||T.blue;
  return(
    <div style={{background:`rgba(${c===T.green?'61,232,154':c===T.amber?'240,192,64':'77,200,240'},.06)`,border:`1px solid ${c}30`,borderRadius:10,padding:'14px 16px',marginBottom:14,display:'flex',gap:12,alignItems:'flex-start'}}>
      <span style={{fontSize:22,flexShrink:0,lineHeight:1.2}}>{icon}</span>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:13,fontWeight:700,color:c,marginBottom:4}}>{title}</div>
        <div style={{fontSize:12,color:T.textSecond,lineHeight:1.65,marginBottom:action?10:0}}>{body}</div>
        {action&&<Btn small onClick={()=>{if(onAction)onAction();dismiss();}} style={{borderColor:c,color:c}}>{action}</Btn>}
      </div>
      <button onClick={dismiss} style={{background:'transparent',border:'none',color:T.textMuted,cursor:'pointer',fontSize:18,padding:'0 2px',lineHeight:1,flexShrink:0}}>×</button>
    </div>
  );
}

// First-visit empty state — shown when a tab has no data yet
function EmptyState({icon,title,body,cta,onCta,xp}){
  return(
    <div style={{textAlign:'center',padding:'40px 20px',maxWidth:420,margin:'0 auto'}}>
      <div style={{fontSize:48,marginBottom:16,lineHeight:1}}>{icon}</div>
      <div style={{fontFamily:"'Syne',sans-serif",fontSize:18,fontWeight:700,marginBottom:10,color:T.textPrimary}}>{title}</div>
      <div style={{fontSize:14,color:T.textSecond,lineHeight:1.7,marginBottom:24}}>{body}</div>
      {cta&&<div>
        <Btn primary onClick={onCta} style={{fontSize:14,padding:'12px 28px'}}>{cta}</Btn>
        {xp&&<div style={{fontSize:11,color:T.blue,marginTop:10}}>+{xp} XP when you complete this</div>}
      </div>}
    </div>
  );
}

// ── TODAY'S HABITS CARD (used on Command tab) ─────────────────────────────────
function TodayHabitsCard({S,onToggle,onSetTab}){
  const ti=todayIdx();
  const active=getActiveHabits(S);
  const done=active.filter(h=>(S.habits[h.k]||[0,0,0,0,0,0,0])[ti]>0);
  const pct=active.length>0?Math.round((done.length/active.length)*100):0;
  const all=pct===100;
  if(active.length===0)return null;
  return(
    <Card style={{borderColor:all?T.greenD:pct>=60?T.blueD:T.borderFaint,cursor:'default'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
        <Slab style={{margin:0}}>{all?'✓ All habits done today':"Today's habits — tap to check off"}</Slab>
        <div style={{display:'flex',gap:10,alignItems:'center'}}>
          <span style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:20,color:all?T.green:pct>0?T.blue:T.textMuted}}>{done.length}/{active.length}</span>
          <button onClick={()=>onSetTab('habits')} style={{background:'transparent',border:'none',color:T.textMuted,cursor:'pointer',fontSize:11,textDecoration:'underline',padding:0}}>Full view</button>
        </div>
      </div>
      <div style={{display:'flex',gap:7,flexWrap:'wrap'}}>
        {active.map(h=>{
          const d=(S.habits[h.k]||[0,0,0,0,0,0,0])[ti]>0;
          return<button key={h.k} onClick={()=>onToggle(h.k,ti)} style={{display:'flex',gap:6,alignItems:'center',padding:'6px 12px',borderRadius:20,border:`1.5px solid ${d?T.greenD:T.borderMid}`,background:d?'rgba(26,122,80,.15)':'transparent',cursor:'pointer',fontFamily:"'Inter',sans-serif",fontSize:12,color:d?T.green:T.textSecond,transition:'all .12s'}}>
            <span>{h.ic||'⭐'}</span><span>{h.l}</span>{d&&<span style={{fontSize:10}}>✓</span>}
          </button>;
        })}
      </div>
      {!all&&pct>0&&<div style={{marginTop:10,fontSize:12,color:T.amber}}>
        {active.length-done.length} habit{active.length-done.length!==1?'s':''} remaining today — finish strong.
      </div>}
    </Card>
  );
}

function CommandTab({S,cf,nw,score,onToggleTask,onToggleHabit,onPayBill,onSetTab}){
  const p1=S.tasks.filter(t=>t.pri===1),p1d=p1.filter(t=>t.done).length;
  const activeHab=getActiveHabits(S);
  const maxHabPts=activeHab.reduce((t,h)=>t+h.pts*7,0)||84;
  const hs=activeHab.reduce((t,h)=>t+(S.habits[h.k]||[0,0,0,0,0,0,0]).reduce((s,v)=>s+v*h.pts,0),0);
  const ti=todayIdx();
  const habDoneToday=activeHab.filter(h=>(S.habits[h.k]||[0,0,0,0,0,0,0])[ti]>0);
  const ub=(S.bills||[]).filter(b=>!b.paid&&daysUntil(b.due)<=7);
  const urgentBills=(S.bills||[]).filter(b=>!b.paid&&daysUntil(b.due)<=3);
  const{c:lvl,n:nxt}=getLvl(S.xp);
  const xpPct=nxt?((S.xp-lvl.xp)/(nxt.xp-lvl.xp))*100:100;
  const ta=WEALTH_ACTIONS[new Date().getDay()%WEALTH_ACTIONS.length];
  const activeGoal=(S.goals||[]).find(g=>!g.completed);
  const todayJournal=(S.journal||[]).find(j=>j.date===todayStr());
  const scoreColor=score>=70?T.green:score>=40?T.amber:T.red;
  const fwa=getFWA(S,cf);const fwaDone=fwa.filter(a=>a.done).length;
  const showFW=fwaDone<fwa.length;
  const setupIncomplete=!cf.cashflowSetup;
  const cashEmpty=!cf.cashflowSetup,wealthEmpty=(S.assets?.items||[]).length===0;
  const tcol=t=>{if(t==='in')return{bg:'#081e14',color:'#3de89a',border:'#1a5a38'};if(t==='sweep')return{bg:'#081828',color:'#4dc8f0',border:'#1a4a6a'};if(t==='bill')return{bg:'#1e0808',color:'#f06070',border:'#6a1a28'};return{bg:'#0e1a24',color:'#7aaac8',border:'#1e3a52'};};

  // Daily directive message — CFO-grade, personalised to actual numbers
  const p1Remaining=p1.filter(t=>!t.done).length;
  const habRemaining=activeHab.length-habDoneToday.length;
  const invRate=cf?.income>0?Math.round(((S.weeklyInvestments||0)/cf.income)*100):0;
  const wksToNeg=cf?.fwd?cf.fwd.findIndex(w=>w.balance<0):-1;
  const safeNwLocal=nw||{investableNet:0};
  const directiveMsg=
    !cf?.cashflowSetup?'Set up your finances in the My Money tab to unlock your personal daily directive — built from your actual numbers.' :
    cf.net<0?`🔴 CRITICAL: Outgoings exceed income by ${fmt(Math.abs(cf.net))}/week. Identify your largest fixed cost and cut it this week. Everything else is secondary.` :
    wksToNeg>=0&&wksToNeg<=2?`⚠ Cash goes negative in ${wksToNeg+1} week${wksToNeg!==0?'s':''}. Open Cashflow now — you have days not weeks.` :
    cf.lowestBal<cf.income?`⚡ Buffer critically thin — 8-week low is ${fmt(cf.lowestBal)}. Hold discretionary spend. Build the buffer first.` :
    (S.weeklyInvestments||0)===0?`Cashflow positive at ${fmt(cf.net)}/week but no sweeps set. Idle cash is a decision. Set a sweep today — even ${fmt(Math.round(cf.net*0.3))}.` :
    invRate<20?`Investment rate ${invRate}% — below the 20% target. You have room. Increase your sweep by ${fmt(Math.round(Math.min(cf.net*0.4,cf.income*0.2-(S.weeklyInvestments||0))))}/week today.` :
    p1Remaining>0?`Strong position — ${fmt(cf.net)}/week, ${invRate}% invested. ${p1Remaining} P1 task${p1Remaining!==1?'s':''} outstanding. Close them.` :
    habRemaining>0?`P1 tasks done. ${habRemaining} habit${habRemaining!==1?'s':''} left. Log them and today is complete.` :
    score>=80?`Exceptional day. Cashflow ${fmt(cf.net)}/week, ${invRate}% invested, habits and tasks complete. The discipline is compounding.` :
    `Good position — ${fmt(cf.net)}/week net, ${invRate}% invested. Keep the daily system running.`;

  return(
    <div>
      {/* ── ZONE 1: DAILY BRIEF HEADER ────────────────────────────── */}
      <div style={{background:score===100?`linear-gradient(135deg,rgba(61,232,154,.08),rgba(77,200,240,.06))`:`linear-gradient(135deg,${T.bgPanel},${T.bgPanel2})`,border:`1px solid ${score===100?T.greenD:scoreColor+'30'}`,borderRadius:12,padding:'16px',marginBottom:14}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:12,gap:12,flexWrap:'wrap'}}>
          <div style={{flex:1}}>
            {score===100&&<div style={{fontSize:11,color:T.green,fontWeight:700,letterSpacing:'.1em',marginBottom:6,textTransform:'uppercase',display:'flex',alignItems:'center',gap:6}}>
              <span>◈</span> Perfect Execution — {new Date().toLocaleDateString('en-AU',{weekday:'long',day:'numeric',month:'short'})} <span>◈</span>
            </div>}
            {score!==100&&<div style={{fontSize:12,letterSpacing:'.08em',color:T.blue,fontWeight:600,marginBottom:8,textTransform:'uppercase',opacity:.85}}>
              Daily Brief — {new Date().toLocaleDateString('en-AU',{weekday:'long',day:'numeric',month:'short'})}
            </div>}
            <div style={{fontSize:15,fontWeight:600,color:T.textPrimary,lineHeight:1.5,maxWidth:480}}>{directiveMsg}</div>
          </div>
          <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:2,flexShrink:0}}>
            <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:40,color:scoreColor,lineHeight:1}}>{score}</div>
            <div style={{fontSize:10,color:T.textMuted,letterSpacing:'.1em',textTransform:'uppercase'}}>Score</div>

            <PBar pct={score} color={scoreColor} height={3}/>
          </div>
        </div>
        {/* Stat strip */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8}}>
          <div onClick={()=>!cashEmpty&&onSetTab('forward')} style={{background:T.bgBase,borderRadius:8,padding:'10px',cursor:cashEmpty?'default':'pointer',border:`1px solid ${!cashEmpty&&cf.net<0?T.redD:T.borderFaint}`,transition:'border-color .15s'}}>
            <div style={{fontSize:9,color:T.textMuted,textTransform:'uppercase',letterSpacing:'.1em',fontFamily:"'DM Mono',monospace",marginBottom:4}}>Weekly Net</div>
            {cashEmpty?<div style={{fontSize:11,color:T.blue,cursor:'pointer'}} onClick={()=>onSetTab('cashflow')}>Set up →</div>
            :<><div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:18,color:cf.net>=0?T.green:T.red,lineHeight:1}}>{fmt(cf.net)}</div>
            <div style={{fontSize:10,color:T.textMuted,marginTop:2}}>{cf.net>=0?'▲ Ahead':'▼ Behind'}</div></>}
          </div>
          <div onClick={()=>!wealthEmpty&&onSetTab('wealth')} style={{background:T.bgBase,borderRadius:8,padding:'10px',cursor:wealthEmpty?'default':'pointer',border:`1px solid ${T.borderFaint}`}}>
            <div style={{fontSize:9,color:T.textMuted,textTransform:'uppercase',letterSpacing:'.1em',fontFamily:"'DM Mono',monospace",marginBottom:4}}>Invested</div>
            {wealthEmpty||!nw?<div style={{fontSize:11,color:T.blue,cursor:'pointer'}} onClick={()=>onSetTab('wealth')}>Log →</div>
            :<><div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:18,color:T.blue,lineHeight:1}}>{fmt(nw.investableNet||nw.net)}</div>
            <div style={{fontSize:10,color:T.textMuted,marginTop:2}}>{S.targetNW>0?`${Math.min(100,((nw.investableNet||nw.net)/S.targetNW)*100).toFixed(0)}% of target`:((parseInt(S.targetYear)||2035)-new Date().getFullYear())+'yr horizon'}</div></>}
          </div>
          <div onClick={()=>onSetTab('habits')} style={{background:T.bgBase,borderRadius:8,padding:'10px',cursor:'pointer',border:`1px solid ${T.borderFaint}`}}>
            <div style={{fontSize:9,color:T.textMuted,textTransform:'uppercase',letterSpacing:'.1em',fontFamily:"'DM Mono',monospace",marginBottom:4}}>Habits</div>
            <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:18,color:hs>=maxHabPts*.6?T.green:hs>=maxHabPts*.3?T.amber:T.red,lineHeight:1}}>{habDoneToday.length}/{activeHab.length}</div>
            <div style={{fontSize:10,color:T.textMuted,marginTop:2}}>Today</div>
          </div>
        </div>
      </div>

      {/* Urgent alerts */}
      {urgentBills.length>0&&<div style={{background:'rgba(240,96,112,.1)',border:`1px solid ${T.redD}`,borderRadius:8,padding:'10px 14px',marginBottom:12,display:'flex',gap:10,alignItems:'center'}}>
        <span style={{fontSize:16,flexShrink:0}}>🚨</span>
        <div style={{flex:1}}><div style={{fontSize:13,fontWeight:700,color:T.red}}>Bill due in 3 days or less</div>
        <div style={{fontSize:12,color:T.textSecond}}>{urgentBills.map(b=>`${b.name} — ${fmt(b.amount)}`).join(' · ')}</div></div>
        <Btn small onClick={()=>onSetTab('bills')} style={{borderColor:T.redD,color:T.red,flexShrink:0}}>Pay →</Btn>
      </div>}
      {cf.cashAlert&&!cashEmpty&&!urgentBills.length&&<div style={{background:'rgba(240,96,112,.08)',border:`1px solid ${T.redD}`,borderRadius:8,padding:'10px 14px',marginBottom:12,display:'flex',gap:10,alignItems:'center'}}>
        <span style={{fontSize:16,flexShrink:0}}>⚠️</span><div style={{flex:1}}><div style={{fontSize:13,fontWeight:600,color:T.red}}>Cash gap in next 8 weeks</div><div style={{fontSize:12,color:T.textSecond}}>Your balance goes negative. Review the 8-week plan.</div></div>
        <Btn small onClick={()=>onSetTab('forward')} style={{borderColor:T.redD,color:T.red,flexShrink:0}}>View →</Btn>
      </div>}

      {/* Onboarding drop-off recovery */}
      {setupIncomplete&&!showFW&&<Card style={{borderColor:T.blueD+'60',marginBottom:14,background:`linear-gradient(135deg,rgba(77,200,240,.04),${T.bgPanel})`}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:12,flexWrap:'wrap'}}>
          <div>
            <div style={{fontSize:14,fontWeight:600,marginBottom:4}}>Finish your setup to unlock your full daily brief</div>
            <div style={{fontSize:12,color:T.textSecond,lineHeight:1.6}}>Add your income and costs in My Money — takes 3 minutes. Your 8-week cashflow projection and personalised directive unlock immediately.</div>
          </div>
          <Btn primary onClick={()=>onSetTab('cashflow')} style={{flexShrink:0}}>Set up now →</Btn>
        </div>
      </Card>}
      {/* First-week setup checklist */}
      {showFW&&<Card style={{borderColor:T.blueD+'50',marginBottom:14}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
          <div><Slab style={{margin:0,marginBottom:4}}>Setup — {fwaDone}/{fwa.length} complete</Slab>
          <div style={{fontSize:12,color:T.textSecond}}>Complete these to get full value from COMMAND.</div></div>
          <span style={{fontSize:18,fontWeight:800,color:T.blue,fontFamily:"'Syne',sans-serif"}}>{Math.round((fwaDone/fwa.length)*100)}%</span>
        </div>
        <PBar pct={(fwaDone/fwa.length)*100} color={`linear-gradient(90deg,${T.blueD},${T.blue})`} height={4}/>
        <div style={{marginTop:10,display:'flex',flexDirection:'column',gap:5}}>
          {fwa.map(a=><div key={a.id} style={{display:'flex',gap:10,alignItems:'center',cursor:'pointer',padding:'6px 8px',borderRadius:6,background:a.done?'rgba(61,232,154,.06)':T.bgPanel2,border:`1px solid ${a.done?T.greenD:T.borderFaint}`}} onClick={()=>!a.done&&onSetTab(a.tab)}>
            <span style={{fontSize:12,flexShrink:0}}>{a.done?'✅':'⭕'}</span>
            <span style={{fontSize:13,color:a.done?T.textMuted:T.textPrimary,textDecoration:a.done?'line-through':'none',flex:1}}>{a.l}</span>
            {!a.done&&<span style={{color:T.blue,fontSize:12,fontWeight:600}}>→</span>}
          </div>)}
        </div>
      </Card>}

      {/* ── ZONE 2: ACTION NOW ─────────────────────────────────────── */}
      <div style={{fontSize:10,letterSpacing:'.15em',color:T.blue,fontFamily:"'DM Mono',monospace",textTransform:'uppercase',marginBottom:8,marginTop:4}}>▶ Action now</div>

      {/* Today's habits — fast tap */}
      <TodayHabitsCard S={S} onToggle={onToggleHabit} onSetTab={onSetTab}/>

      {/* P1 tasks */}
      <Card style={{borderColor:p1.length>0&&p1d<p1.length?T.amberD+'60':T.borderFaint}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:p1.length?10:0}}>
          <Slab style={{margin:0}}>P1 Tasks — Today</Slab>
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            {p1.length>0&&<span style={{fontSize:11,color:p1d===p1.length?T.green:T.amber,fontWeight:600}}>{p1d}/{p1.length}</span>}
            <Btn small onClick={()=>onSetTab('tasks')}>+ Add</Btn>
          </div>
        </div>
        {p1.length===0
          ?<div style={{fontSize:13,color:T.textMuted,padding:'4px 0'}}>No P1 tasks set. <span style={{color:T.blue,cursor:'pointer',fontWeight:600}} onClick={()=>onSetTab('tasks')}>Add what must get done today →</span></div>
          :p1.map(t=><Row key={t.id} style={{opacity:t.done?.5:1,borderLeft:`2px solid ${t.done?T.greenD:T.amber}40`}} onClick={()=>onToggleTask(t.id)}>
            <CheckBtn done={t.done} onClick={e=>{e.stopPropagation();onToggleTask(t.id);}}/>
            <span style={{flex:1,fontSize:13,textDecoration:t.done?'line-through':'none',color:t.done?T.textMuted:T.textPrimary}}>{t.text}</span>
            <Pill>{t.area}</Pill>
          </Row>)
        }
      </Card>

      {/* Active goal */}
      {activeGoal
        ?<Card style={{borderColor:'rgba(176,106,240,.3)',cursor:'pointer'}} onClick={()=>onSetTab('goals')}>
          <div style={{display:'flex',gap:12,alignItems:'flex-start'}}>
            <span style={{fontSize:20,flexShrink:0}}>🎯</span>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:700,marginBottom:4,color:T.textPrimary,lineHeight:1.4}}>{activeGoal.declaration}</div>
              <div style={{fontSize:12,color:'#b06af0',marginBottom:8,fontStyle:'italic'}}>Today: {activeGoal.dailyAction}</div>
              <PBar pct={activeGoal.progress||0} color="#b06af0" height={4}/>
              <div style={{fontSize:11,color:T.textMuted,marginTop:4}}>{activeGoal.progress||0}% complete</div>
            </div>
            <span style={{color:T.textMuted,fontSize:16,flexShrink:0}}>→</span>
          </div>
        </Card>
        :<Card style={{cursor:'pointer',border:`1px dashed ${T.borderFaint}`}} onClick={()=>onSetTab('goals')}>
          <div style={{display:'flex',gap:12,alignItems:'center'}}><span style={{fontSize:22}}>🎯</span><div><div style={{fontSize:13,fontWeight:600}}>No active goal</div><div style={{fontSize:12,color:T.textMuted}}>Set a goal — takes 2 minutes. Earn 20 XP.</div></div><span style={{marginLeft:'auto',color:T.blue,fontSize:14,fontWeight:700}}>Set goal →</span></div>
        </Card>
      }

      {/* ── ZONE 3: AHEAD ─────────────────────────────────────────── */}
      <div style={{fontSize:10,letterSpacing:'.15em',color:T.blue,fontFamily:"'DM Mono',monospace",textTransform:'uppercase',marginBottom:8,marginTop:4}}>◎ Ahead this week</div>

      {/* Weekly directives */}
      <Card>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:cf.dirs.length?10:0}}>
          <Slab style={{margin:0}}>This week's directives</Slab>
          {cf.dirs.length>0&&<Btn small onClick={()=>onSetTab('forward')}>8-week plan →</Btn>}
        </div>
        {cf.dirs.length===0
          ?<div style={{fontSize:13,color:T.textMuted}}>Set up cashflow to generate weekly directives. <span style={{color:T.blue,cursor:'pointer',fontWeight:600}} onClick={()=>onSetTab('cashflow')}>Set up →</span></div>
          :cf.dirs.slice(0,6).map((d,i)=>{const c=tcol(d.type);return<Row key={i} style={{borderLeft:`3px solid ${c.border}`}}>
            <span style={{fontSize:10,padding:'2px 6px',borderRadius:4,background:c.bg,color:c.color,minWidth:46,textAlign:'center',fontFamily:"'DM Mono',monospace",fontWeight:600,flexShrink:0}}>{d.day}</span>
            <span style={{flex:1,fontSize:12,color:T.textSecond}}>{d.action}</span>
            <span style={{fontSize:12,color:d.type==='in'?T.green:d.type==='bill'?T.red:T.textSecond,fontWeight:700,flexShrink:0,fontFamily:"'DM Mono',monospace"}}>{fmt(d.amount)}</span>
          </Row>;})}
      </Card>

      {/* Upcoming bills */}
      {ub.length>0&&<Card style={{borderColor:T.redD+'60'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
          <Slab style={{margin:0,color:T.red}}>Bills due within 7 days</Slab>
          <Btn small onClick={()=>onSetTab('bills')}>All bills →</Btn>
        </div>
        {ub.map(b=><Row key={b.id} style={{borderLeft:`3px solid ${daysUntil(b.due)<=3?T.red:T.amber}`}}>
          <span style={{flex:1,fontSize:13}}>{b.name}</span>
          <span style={{fontSize:13,color:T.red,fontWeight:700,marginRight:8,fontFamily:"'DM Mono',monospace"}}>{fmt(b.amount)}</span>
          <span style={{fontSize:11,color:T.textMuted,marginRight:8}}>{daysUntil(b.due)}d</span>
          <Btn green small onClick={()=>onPayBill(b.id)}>Pay</Btn>
        </Row>)}
      </Card>}

      {/* Wealth action + journal */}
      <Grid2>
        <Card style={{cursor:'pointer'}} onClick={()=>onSetTab('rewards')}>
          <Slab>Wealth move — today</Slab>
          <div style={{display:'flex',gap:8,alignItems:'flex-start',marginBottom:12}}>
            <span style={{padding:'3px 8px',background:'rgba(58,133,176,.15)',border:`1px solid ${T.blueD}`,borderRadius:4,fontSize:10,color:T.blue,whiteSpace:'nowrap',fontWeight:600,flexShrink:0}}>{ta.area}</span>
            <span style={{fontSize:12,color:T.textSecond,lineHeight:1.5}}>{ta.action}</span>
          </div>
          <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
            <span style={{fontSize:11,color:T.textMuted}}>Lv.{lvl.l} {lvl.t}</span>
            {nxt&&<span style={{fontSize:11,color:T.textMuted}}>{nxt.xp-S.xp} XP → {nxt.t}</span>}
          </div>
          <PBar pct={xpPct} color={`linear-gradient(90deg,${T.blueD},${T.blue})`} height={4}/>
        </Card>
        {todayJournal
          ?<Card style={{borderColor:T.greenD+'40'}}><div style={{display:'flex',gap:10,alignItems:'center'}}><span style={{fontSize:22}}>✅</span><div><div style={{fontSize:13,fontWeight:600,color:T.green}}>Journal done</div><div style={{fontSize:12,color:T.textMuted}}>Streak going. See you tomorrow.</div></div></div></Card>
          :<Card style={{cursor:'pointer',border:`1px dashed ${T.borderFaint}`}} onClick={()=>onSetTab('journal')}>
            <div style={{display:'flex',gap:10,alignItems:'center'}}><span style={{fontSize:22}}>📓</span><div><div style={{fontSize:13,fontWeight:600}}>Journal — not done</div><div style={{fontSize:12,color:T.textMuted}}>2 minutes. 10 XP. Build the streak.</div></div><span style={{marginLeft:'auto',color:T.blue,fontWeight:700,fontSize:14}}>→</span></div>
          </Card>
        }
      </Grid2>
    </div>
  );
}

const FREQ_OPTS=['weekly','fortnightly','monthly','quarterly','annual'];
const FreqSel=({val,onChange,style={}})=>(<select value={val||'weekly'} onChange={e=>onChange(e.target.value)} style={{background:T.bgPanel2,border:`1px solid ${T.borderInput}`,borderRadius:6,color:T.textSecond,fontSize:11,padding:'4px 8px',cursor:'pointer',outline:'none',fontFamily:"'DM Sans',sans-serif",...style}}>{FREQ_OPTS.map(f=><option key={f} value={f}>{f.charAt(0).toUpperCase()+f.slice(1)}</option>)}</select>);

function CashflowTab({S,cf,onInc,onOB,onInv,onAddFixed,onDelFixed,onUpdFixed}){
  const [showAdd,setShowAdd]=useState(false);
  const [nf,setNf]=useState({name:'',amount:0,freq:'weekly'});
  const freqOpts=FREQ_OPTS;
  const toW=(amt,freq)=>{const m={weekly:1,fortnightly:0.5,monthly:12/52,quarterly:4/52,annual:1/52};return(amt||0)*(m[freq||'weekly']||1);};
  const netColor=cf.net>0?T.green:cf.net<0?T.red:T.textMuted;
  const netBg=cf.net>0?'#08130e':cf.net<0?'#130808':T.bgPanel;
  const stepsComplete=[
    (S.openingBalance||0)>0,(cf.income||0)>0,
    (S.weeklyFixed||[]).length>0,(S.weeklyInvestments||0)>0
  ].filter(Boolean).length;
  return(
    <div>
      <ContextTip
        id="cashflow_intro"
        icon="⬡"
        title="This is your financial engine — set it up first"
        body="Enter your income, fixed costs, and investment sweeps below. Once done, COMMAND generates your 8-week cash projection and daily action directives automatically. Update your opening balance every Monday morning."
        action="Got it"
        color={T.blue}
      />
      {/* LIVE NET POSITION */}
      <Card style={{background:cf.cashflowSetup?netBg:T.bgPanel,borderColor:cf.cashflowSetup?(cf.net>=0?T.greenD:T.redD):T.borderFaint,marginBottom:16}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:12}}>
          <div>
            <Slab>{!cf.cashflowSetup?`Complete ${4-stepsComplete} more step${4-stepsComplete!==1?'s':''} to see your position`:cf.net>=0?'✓ Weekly net — you are ahead':'⚠ Weekly net — action required'}</Slab>
            <NUM size={42} color={cf.cashflowSetup?netColor:T.textMuted}>{cf.cashflowSetup?`${fmt(cf.net)}/wk`:'—'}</NUM>
            {cf.cashflowSetup&&<div style={{fontSize:12,color:T.textMuted,marginTop:6}}>Annual surplus: <span style={{color:cf.net>=0?T.green:T.red,fontWeight:600}}>{fmt(cf.net*52)}</span> · Invested/yr: <span style={{color:T.blue,fontWeight:600}}>{fmt((S.weeklyInvestments||0)*52)}</span></div>}
          </div>
          {cf.cashflowSetup&&<div style={{textAlign:'right',flexShrink:0}}>
            <div style={{fontSize:11,color:T.textMuted,marginBottom:2}}>In /wk</div>
            <NUM size={18} color={T.green}>{fmt(cf.income)}</NUM>
            <div style={{fontSize:11,color:T.textMuted,margin:'8px 0 2px'}}>Out + Sweeps</div>
            <NUM size={18} color={T.red}>{fmt(cf.fixed+cf.inv)}</NUM>
          </div>}
        </div>
        {cf.cashflowSetup&&cf.net<0&&<div style={{marginTop:12,padding:'10px 14px',background:'rgba(240,96,112,.08)',borderRadius:8,fontSize:13,color:T.red,borderTop:`1px solid ${T.redD}`}}>
          ⚠ Outgoings exceed income by <strong>{fmt(Math.abs(cf.net))}/week</strong> — that is <strong>{fmt(Math.abs(cf.net)*52)}/year</strong> in deficit. Identify one fixed cost to cut or one income source to increase.
        </div>}
        {cf.cashflowSetup&&cf.net>0&&cf.net<200&&<div style={{marginTop:12,padding:'10px 14px',background:'rgba(240,96,112,.08)',borderRadius:8,fontSize:13,color:T.red,borderTop:`1px solid ${T.redD}`}}>
          ⚠ Very thin surplus — {fmt(cf.net)}/week. One unexpected bill pushes you negative. Cut a fixed cost or increase income before adding sweeps.
        </div>}
        {cf.cashflowSetup&&cf.net>=200&&cf.net<500&&<div style={{marginTop:12,padding:'10px 14px',background:'rgba(240,192,64,.08)',borderRadius:8,fontSize:13,color:T.amber,borderTop:`1px solid ${T.amberD}`}}>
          Manageable but lean — {fmt(cf.net)}/week. Review fixed costs to build a stronger buffer before increasing investment sweeps.
        </div>}
        {cf.cashflowSetup&&cf.net>=500&&cf.net<1000&&<div style={{marginTop:12,padding:'10px 14px',background:'rgba(77,200,240,.08)',borderRadius:8,fontSize:13,color:T.blue,borderTop:`1px solid ${T.blueD}`}}>
          Good position — {fmt(cf.net)}/week. Are your investment sweeps maximised? Every extra dollar deployed weekly compounds at scale.
        </div>}
        {cf.cashflowSetup&&cf.net>=1000&&<div style={{marginTop:12,padding:'10px 14px',background:'rgba(61,232,154,.06)',borderRadius:8,fontSize:13,color:T.green,borderTop:`1px solid ${T.greenD}`}}>
          ✓ Strong surplus — {fmt(cf.net)}/week ({fmt(cf.net*52)}/yr). Ensure sweeps are maximised — excess cash in an operating account is a compounding opportunity missed.
        </div>}
        {!cf.cashflowSetup&&<div style={{marginTop:10}}><PBar pct={(stepsComplete/4)*100} color={`linear-gradient(90deg,${T.blueD},${T.blue})`} height={4}/><div style={{fontSize:11,color:T.textMuted,marginTop:5}}>{stepsComplete} of 4 steps complete</div></div>}
      </Card>
      <Grid2>
        <div>
          <Card style={{borderColor:(S.openingBalance||0)>0?T.greenD+'40':T.borderFaint}}>
            <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:10}}><span style={{fontSize:15}}>{(S.openingBalance||0)>0?'✅':'📅'}</span><Slab style={{margin:0}}>Opening balance — update every Monday</Slab></div>
            <div style={{fontSize:11,color:T.textMuted,marginBottom:8}}>Open your banking app right now. Enter your main account balance.</div>
            <input type="number" value={S.openingBalance||''} onChange={e=>onOB(e.target.value)} className="num-input" style={{width:'100%',fontSize:16,fontWeight:600,padding:'10px 14px'}} placeholder="e.g. 8500"/>
            {cf.cashflowSetup&&(S.openingBalance||0)>0&&<div style={{fontSize:12,color:T.textMuted,marginTop:8}}>8-week low: <span style={{color:cf.lowestBal>=0?T.green:T.red,fontWeight:600}}>{fmt(cf.lowestBal)}</span></div>}
          </Card>
          <Card style={{borderColor:cf.income>0?T.greenD+'40':T.borderFaint}}>
            <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:12}}><span style={{fontSize:15}}>{cf.income>0?'✅':'💚'}</span><Slab style={{margin:0}}>Income — update weekly</Slab></div>
            {[{k:'yours',fk:'yoursFreq',l:'Your take-home pay',def:'fortnightly'},{k:'partner',fk:'partnerFreq',l:"Partner's income",def:'fortnightly'},{k:'other',fk:'otherFreq',l:'Other / irregular',def:'weekly'}].map(r=>(
              <div key={r.k} style={{marginBottom:12}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
                  <label style={{fontSize:12,color:T.textSecond}}>{r.l}</label>
                  <FreqSel val={S.income[r.fk]||r.def} onChange={v=>onInc(r.fk,v)}/>
                </div>
                <input type="number" value={S.income[r.k]||''} onChange={e=>onInc(r.k,e.target.value)} className="num-input" style={{width:'100%'}} placeholder="0"/>
                {(S.income[r.k]||0)>0&&<div style={{fontSize:10,color:T.textMuted,marginTop:4}}>{fmt(toW(S.income[r.k],S.income[r.fk]||r.def))}/week</div>}
              </div>
            ))}
            <div style={{borderTop:`1px solid ${T.borderFaint}`,marginTop:4,paddingTop:8,display:'flex',justifyContent:'space-between'}}>
              <span style={{fontSize:12,fontWeight:600}}>Total</span>
              <span style={{color:T.green,fontWeight:700}}>{fmt(cf.income)}/wk · {fmt(cf.income*52)}/yr</span>
            </div>
          </Card>
          <Card style={{borderColor:(S.weeklyInvestments||0)>0?T.blueD+'60':T.borderFaint}}>
            <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:10}}><span style={{fontSize:15}}>{(S.weeklyInvestments||0)>0?'✅':'🔁'}</span><Slab style={{margin:0}}>Weekly investment sweeps</Slab></div>
            <div style={{fontSize:11,color:T.textMuted,marginBottom:8}}>Shares, index funds, super, offset account — anything that builds wealth.</div>
            <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:6}}>
              <input type="number" value={S.weeklyInvestments||''} onChange={e=>onInv(e.target.value)} className="num-input" style={{flex:1}} placeholder="e.g. 500"/>
              <FreqSel val={S.investmentsFreq||'weekly'} onChange={v=>onInv(v,true)}/>
            </div>
            {(S.weeklyInvestments||0)>0&&<>
              <div style={{fontSize:11,color:T.blue,marginTop:4,fontWeight:500}}>{fmt(cf.inv*52)}/year compounding ({fmt(cf.inv)}/wk)</div>
              {cf.income>0&&<div style={{fontSize:11,color:T.textMuted,marginTop:4}}>Investment rate: <span style={{color:(cf.inv/cf.income)>=0.2?T.green:T.amber,fontWeight:600}}>{Math.round((cf.inv/cf.income)*100)}% of income</span> — {(cf.inv/cf.income)>=0.2?'✓ disciplined':'target 20%+'}</div>}
            </>}
          </Card>
        </div>
        <div>
          <Card>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
              <div style={{display:'flex',gap:8,alignItems:'center'}}><span style={{fontSize:15}}>{(S.weeklyFixed||[]).length>0?'✅':'💸'}</span><Slab style={{margin:0}}>Weekly fixed outgoings</Slab></div>
              <Btn small onClick={()=>setShowAdd(!showAdd)}>+ Add</Btn>
            </div>
            <div style={{fontSize:11,color:T.textMuted,marginBottom:12}}>Mortgage/rent, loan repayments, insurance, subscriptions — everything out weekly.</div>
            {showAdd&&<div style={{background:T.bgPanel2,border:`1px solid ${T.borderInput}`,borderRadius:8,padding:12,marginBottom:12}}>
              <input className="cmd-input" placeholder="e.g. Mortgage, Car loan, Insurance" value={nf.name} onChange={e=>setNf(p=>({...p,name:e.target.value}))} style={{padding:'9px 12px',fontSize:13,marginBottom:8,display:'block',width:'100%'}}/>
              <div style={{display:'flex',gap:8,marginBottom:8,alignItems:'center'}}>
                <div style={{position:'relative',flex:1}}><span style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:T.textMuted,fontSize:13}}>$</span><input className="cmd-input" type="number" placeholder="0" value={nf.amount||''} onChange={e=>setNf(p=>({...p,amount:parseFloat(e.target.value)||0}))} style={{paddingLeft:24,padding:'9px 8px 9px 24px',fontSize:13,width:'100%'}}/></div>
                <FreqSel val={nf.freq||'monthly'} onChange={v=>setNf(p=>({...p,freq:v}))}/>
              </div>
              {nf.amount>0&&<div style={{fontSize:11,color:T.textMuted,marginBottom:8}}>{fmt(toW(nf.amount,nf.freq||'monthly'))}/week</div>}
              <div style={{display:'flex',gap:8}}><Btn primary small onClick={()=>{if(nf.name&&nf.amount>0){onAddFixed({name:nf.name,amount:nf.amount,freq:nf.freq||'monthly'});setNf({name:'',amount:0,freq:'monthly'});setShowAdd(false);}}}>Save</Btn><Btn small onClick={()=>setShowAdd(false)}>Cancel</Btn></div>
            </div>}
            {(S.weeklyFixed||[]).length===0&&!showAdd&&<div style={{textAlign:'center',padding:'16px 0',color:T.textMuted,fontSize:12}}><div style={{fontSize:20,marginBottom:6}}>💸</div>Start with your biggest fixed cost — mortgage or rent.</div>}
            {(S.weeklyFixed||[]).map((r,i)=>(
              <div key={i} style={{marginBottom:10,paddingBottom:10,borderBottom:`1px solid ${T.borderFaint}`}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
                  <span style={{fontSize:13,color:T.textSecond,flex:1}}>{r.name==='Credit Card / Weekly Spend'?'Discretionary / Living':r.name}</span>
                  <button onClick={()=>onDelFixed(i)} style={{background:'transparent',border:'none',color:T.textMuted,cursor:'pointer',fontSize:16,padding:'0 4px',lineHeight:1}}>×</button>
                </div>
                <div style={{display:'flex',gap:8,alignItems:'center'}}>
                  <div style={{position:'relative',flex:1}}><span style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:T.textMuted,fontSize:12}}>$</span><input type="number" value={r.amount||''} onChange={e=>onUpdFixed(i,parseFloat(e.target.value)||0,r.freq)} className="num-input" style={{paddingLeft:22}}/></div>
                  <FreqSel val={r.freq||'weekly'} onChange={v=>onUpdFixed(i,r.amount,v)}/>
                </div>
                {r.amount>0&&(r.freq&&r.freq!=='weekly')&&<div style={{fontSize:10,color:T.textMuted,marginTop:4}}>{fmt(toW(r.amount,r.freq))}/week</div>}
              </div>
            ))}
            {(S.weeklyFixed||[]).length>0&&<>
              <div style={{display:'flex',justifyContent:'space-between',marginTop:4,paddingTop:8,borderTop:`1px solid ${T.borderMid}`}}><span style={{fontSize:12,fontWeight:600}}>Weekly total</span><span style={{color:T.red,fontWeight:700}}>{fmt(cf.fixed)}/wk · {fmt(cf.fixed*52)}/yr</span></div>
              {cf.income>0&&<div style={{marginTop:6,fontSize:12,color:T.textMuted}}>Fixed cost ratio: <span style={{color:(cf.fixed/cf.income)>0.7?T.red:(cf.fixed/cf.income)>0.5?T.amber:T.green,fontWeight:600}}>{Math.round((cf.fixed/cf.income)*100)}% of income</span>{(cf.fixed/cf.income)>0.7?' — dangerously high':(cf.fixed/cf.income)>0.5?' — review for cuts':' — healthy'}</div>}
            </>}
          </Card>
        </div>
      </Grid2>
    </div>
  );
}
function ForwardTab({S,cf}){
  const tcol=t=>{if(t==='in')return{bg:'#081e14',color:'#3de89a',border:'#1a5a38'};if(t==='sweep')return{bg:'#081828',color:'#4dc8f0',border:'#1a4a6a'};if(t==='bill')return{bg:'#1e0808',color:'#f06070',border:'#6a1a28'};return{bg:'#0e1a24',color:'#7aaac8',border:'#1e3a52'};};
  if(!cf.cashflowSetup)return(<div><ContextTip id="forward_setup" icon="◎" title="Set up Cashflow first to unlock your 8-week projection" body="Once you've entered your income and outgoings in the Cashflow tab, COMMAND projects your cash position 8 weeks ahead — with traffic lights showing when gaps appear before they become problems." color={T.blue}/><Card style={{textAlign:'center',padding:32}}><div style={{fontSize:32,marginBottom:12}}>⬡</div><div style={{fontSize:15,fontWeight:600,marginBottom:8}}>Cashflow not set up yet</div><div style={{fontSize:13,color:T.textMuted}}>Set up your income and outgoings in the Cashflow tab first.</div></Card></div>);
  const mx=Math.max(...cf.fwd.map(w=>Math.abs(w.balance)),1);
  const wkColor=wk=>wk.balance<0?T.red:wk.balance<cf.income?T.amber:T.green;
  const wkLabel=wk=>wk.balance<0?'⚠ Negative':wk.balance<cf.income?'⚡ Tight':'✓ Healthy';
  return(
    <div>
      {/* SWEEP DIRECTIVE — most important action this week */}
      <Card style={{borderColor:T.blueD,marginBottom:14}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
          <Slab style={{margin:0}}>This week — execute in this order</Slab>
          <span style={{fontSize:11,color:T.textMuted,background:T.bgPanel2,padding:'3px 8px',borderRadius:4,border:`1px solid ${T.borderFaint}`}}>8-week view ↓</span>
        </div>
        {cf.dirs.length===0?<div style={{fontSize:13,color:T.textMuted}}>Set up cashflow to see your directives.</div>
        :cf.dirs.map((d,i)=>{const c=tcol(d.type);return<div key={i} style={{display:'flex',gap:12,alignItems:'center',padding:'10px 12px',borderRadius:8,background:T.bgPanel2,border:`1px solid ${c.border}`,marginBottom:6,borderLeft:`4px solid ${c.border}`}}>
          <div style={{width:52,flexShrink:0,textAlign:'center'}}>
            <div style={{fontSize:10,padding:'3px 6px',borderRadius:4,background:c.bg,color:c.color,fontFamily:"'DM Mono',monospace",fontWeight:700}}>{d.day}</div>
          </div>
          <div style={{flex:1}}>
            <div style={{fontSize:13,fontWeight:500}}>{d.action}</div>
          </div>
          <div style={{fontSize:14,color:d.type==='in'?T.green:d.type==='bill'?T.red:T.textSecond,fontWeight:700,flexShrink:0}}>{fmt(d.amount)}</div>
        </div>;})
        }
        {cf.urgBills&&cf.urgBills.length>0&&<div style={{marginTop:8,padding:'10px 12px',background:'rgba(240,96,112,.08)',border:`1px solid ${T.redD}`,borderRadius:8,fontSize:13,color:T.red}}>
          ⚠ {cf.urgBills.length} urgent bill{cf.urgBills.length!==1?'s':''} due within 7 days — {fmt(cf.urgBills.reduce((s,b)=>s+b.amount,0))} total
        </div>}
      </Card>

      {/* 8-WEEK PROJECTION with traffic lights and gap advisory */}
      <Card>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
          <div>
            <Slab style={{margin:0,marginBottom:4}}>8-Week cash projection</Slab>
            <div style={{fontSize:12,color:T.textMuted}}>Opening {fmt(S.openingBalance||0)} · {fmt(cf.net)}/wk base surplus</div>
          </div>
          <div style={{textAlign:'right'}}>
            <div style={{fontSize:11,color:T.textMuted,marginBottom:2}}>8-week low</div>
            <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:20,color:cf.lowestBal>=0?T.green:T.red}}>{fmt(cf.lowestBal)}</div>
          </div>
        </div>
        {cf.cashAlert&&<div style={{marginBottom:12,padding:'10px 14px',background:'rgba(240,96,112,.1)',border:`1px solid ${T.redD}`,borderRadius:8,fontSize:13,color:T.red}}>
          ⚠ Your balance goes negative in the next 8 weeks. Review bills and consider deferring discretionary spending.
        </div>}
        {cf.fwd.map((wk,i)=>{
          const neg=wk.balance<0,tight=wk.balance>0&&wk.balance<cf.income,now=i===0;
          const bc=neg?T.redD:tight?T.amberD:now?T.blueD:T.borderFaint;
          return<div key={i} style={{marginBottom:10,padding:'10px 12px',background:T.bgPanel2,borderRadius:8,border:`1px solid ${bc}`}}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:6,alignItems:'center',flexWrap:'wrap',gap:4}}>
              <div style={{display:'flex',gap:8,alignItems:'center'}}>
                <span style={{fontSize:11,fontWeight:600,color:neg?T.red:tight?T.amber:now?T.blue:T.textSecond}}>{wk.label}</span>
                <span style={{fontSize:11,color:T.textMuted}}>{wk.start}–{wk.end}</span>
                {now&&<span style={{fontSize:10,color:T.blue,background:'rgba(77,200,240,.1)',padding:'2px 7px',borderRadius:10,fontWeight:600}}>THIS WEEK</span>}
              </div>
              <div style={{display:'flex',gap:12,alignItems:'center'}}>
                <span style={{fontSize:10,padding:'2px 8px',borderRadius:10,background:neg?'rgba(240,96,112,.15)':tight?'rgba(240,192,64,.12)':'rgba(61,232,154,.08)',color:wkColor(wk),fontWeight:600}}>{wkLabel(wk)}</span>
                {wk.bt>0&&<span style={{fontSize:11,color:T.amber}}>Bills −{fmt(wk.bt)}</span>}
                <span style={{fontFamily:"'Syne',sans-serif",fontSize:16,fontWeight:800,color:neg?T.red:T.green}}>{fmt(wk.balance)}</span>
              </div>
            </div>
            <PBar pct={Math.max(5,Math.min(100,(wk.balance/mx+1)*50))} color={neg?T.redD:tight?T.amberD:now?T.blueD:T.greenD} height={4}/>
            {wk.bills.length>0&&<div style={{marginTop:8,display:'flex',gap:6,flexWrap:'wrap'}}>{wk.bills.map(b=><span key={b.id} style={{fontSize:11,background:'rgba(240,96,112,.1)',color:T.red,padding:'2px 8px',borderRadius:4,border:`1px solid ${T.redD}`}}>{b.name}{b.amount>0?` — ${fmt(b.amount)}`:''}</span>)}</div>}
            {neg&&<div style={{marginTop:8,fontSize:12,color:T.red}}>Gap advisory: You need {fmt(Math.abs(wk.balance))} more in this week — defer a bill, increase income, or draw from reserves.</div>}
            {tight&&!neg&&<div style={{marginTop:8,fontSize:12,color:T.amber}}>Buffer warning: Less than one week of income in reserve — hold discretionary spending this week.</div>}
          </div>;
        })}
      </Card>
    </div>
  );
}
function BillsTab({S,onToggleBill,onAddBill}){
  const [show,setShow]=useState(false);
  const [form,setForm]=useState({name:'',amount:'',due:'',cat:'Other'});
  const sub=()=>{if(!form.name||!form.due)return;onAddBill({name:form.name,amount:parseFloat(form.amount)||0,due:form.due,cat:form.cat});setForm({name:'',amount:'',due:'',cat:'Other'});setShow(false);};
  const sorted=[...(S.bills||[])].sort((a,b)=>new Date(a.due)-new Date(b.due));
  return(
    <div>
      <Card>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
          <div><Slab style={{margin:0}}>Bills & upcoming payments</Slab><div style={{fontSize:12,color:T.textMuted,marginTop:4}}>Unpaid: <span style={{color:T.red,fontWeight:600}}>{fmt(sorted.filter(b=>!b.paid).reduce((s,b)=>s+b.amount,0))}</span></div></div>
          <Btn primary onClick={()=>setShow(!show)}>+ Add bill</Btn>
        </div>
        {show&&<div style={{background:T.bgPanel2,border:`1px solid ${T.borderInput}`,borderRadius:10,padding:16,marginBottom:14}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
            <div><label style={{fontSize:11,color:T.textMuted,display:'block',marginBottom:4}}>Bill name</label><input placeholder="e.g. Car insurance" value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} className="cmd-input" style={{padding:'9px 12px',fontSize:13}}/></div>
            <div><label style={{fontSize:11,color:T.textMuted,display:'block',marginBottom:4}}>Amount ($)</label><input type="number" placeholder="0" value={form.amount} onChange={e=>setForm(p=>({...p,amount:e.target.value}))} className="cmd-input" style={{padding:'9px 12px',fontSize:13}}/></div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
            <div><label style={{fontSize:11,color:T.textMuted,display:'block',marginBottom:4}}>Due date</label><DatePicker value={form.due} onChange={v=>setForm(p=>({...p,due:v}))} placeholder="Select due date"/></div>
            <div style={{display:'flex',alignItems:'flex-end'}}><Btn primary onClick={sub} style={{width:'100%',padding:'9px',justifyContent:'center'}}>Save bill</Btn></div>
          </div>
        </div>}
        {sorted.length===0?<div>
          <ContextTip
            id="bills_intro"
            icon="📅"
            title="Add your upcoming bills and they appear in your 8-week projection"
            body="Car rego, insurance, school fees, quarterly bills — anything with a due date. COMMAND places each one in the correct week of your cash projection so you can see exactly when money gets tight before it happens."
            action="Add my first bill"
            onAction={()=>setShow(true)}
            color={T.amber}
          />
          <div style={{textAlign:'center',padding:'20px 0',color:T.textMuted}}><div style={{fontSize:20,marginBottom:6}}>≡</div><div style={{fontSize:13}}>No bills added yet.</div></div>
        </div>
        :<div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead><tr style={{borderBottom:`1px solid ${T.borderMid}`}}>{['Bill','Amount','Due','Days','Status',''].map(h=><th key={h} style={{padding:'6px 10px',textAlign:h==='Amount'?'right':'left',fontSize:10,letterSpacing:'.1em',color:T.textMuted,fontWeight:500}}>{h.toUpperCase()}</th>)}</tr></thead>
            <tbody>{sorted.map(b=>{const d=daysUntil(b.due),urg=!b.paid&&d<=7,soon=!b.paid&&d<=14&&!urg;return<tr key={b.id} style={{borderBottom:`1px solid ${T.borderFaint}`,opacity:b.paid?.5:1}}>
              <td style={{padding:'8px 10px',fontWeight:500}}>{b.name}</td>
              <td style={{padding:'8px 10px',textAlign:'right',color:b.amount>0?T.red:T.textMuted,fontWeight:600}}>{b.amount>0?fmt(b.amount):'Review'}</td>
              <td style={{padding:'8px 10px',color:T.textMuted,fontSize:12}}>{b.due}</td>
              <td style={{padding:'8px 10px',color:urg?T.red:soon?T.amber:T.textMuted,fontWeight:urg||soon?600:400}}>{d}d</td>
              <td style={{padding:'8px 10px'}}><span style={{fontSize:11,padding:'3px 8px',borderRadius:4,background:b.paid?'rgba(26,122,80,.15)':urg?'rgba(240,96,112,.15)':soon?'rgba(240,192,64,.12)':'rgba(74,112,144,.12)',color:b.paid?T.green:urg?T.red:soon?T.amber:T.textMuted,border:`1px solid ${b.paid?T.greenD:urg?T.redD:soon?T.amberD:T.borderFaint}`}}>{b.paid?'Paid':urg?'Urgent':soon?'Soon':'Upcoming'}</span></td>
              <td style={{padding:'8px 10px'}}><Btn green={!b.paid} small onClick={()=>onToggleBill(b.id)}>{b.paid?'Unpay':'Pay'}</Btn></td>
            </tr>;})}
            </tbody>
          </table>
        </div>}
      </Card>
    </div>
  );
}

function TypeSection({type,label,items,ed,na,setNa,getAssetVal,setAssetBuf,flushAsset,onAddAsset,onDelAsset}){
    if(items.length===0&&!ed)return null;
    const total=items.reduce((s,a)=>s+(a.value||0),0);
    const isInvestable=INVESTABLE_TYPES.includes(type);
    const rate=ASSET_GROWTH_RATES[type]||0.05;
    return(
      <div style={{marginBottom:14}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
          <div style={{fontSize:12,fontWeight:600,color:isInvestable?T.blue:T.textMuted}}>{label}</div>
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            <span style={{fontSize:11,color:T.textMuted}}>{(rate*100).toFixed(0)}%/yr est.</span>
            {isInvestable?<span style={{fontSize:10,padding:'2px 7px',borderRadius:4,background:'rgba(77,200,240,.1)',color:T.blue,fontWeight:600}}>COUNTS TOWARD TARGET</span>
            :<span style={{fontSize:10,padding:'2px 7px',borderRadius:4,background:T.bgPanel2,color:T.textMuted,fontWeight:500}}>excluded from target</span>}
          </div>
        </div>
        {items.map((a,i)=>(
          <div key={a.id||i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6,paddingBottom:6,borderBottom:`1px solid ${T.borderFaint}`}}>
            <span style={{fontSize:13,color:T.textSecond,flex:1}}>{a.name}</span>
            {ed?<><input type="number" value={getAssetVal(items.indexOf(a),'v',a.value)||''} onChange={e=>setAssetBuf(p=>({...p,[`${items.indexOf(a)}_v`]:e.target.value}))} onBlur={e=>flushAsset(items.indexOf(a),'v',e.target.value)} className="num-input" style={{width:110,marginRight:8}}/><button onClick={()=>onDelAsset(items.indexOf(a))} style={{background:'transparent',border:'none',color:T.textMuted,cursor:'pointer',fontSize:14}}>×</button></>
            :<span style={{fontSize:13,fontWeight:600,color:isInvestable?T.green:T.textMuted}}>{fmt(a.value||0)}</span>}
          </div>
        ))}
        {items.length>0&&<div style={{display:'flex',justifyContent:'space-between',paddingTop:4}}><span style={{fontSize:11,color:T.textMuted}}>Subtotal</span><span style={{fontSize:12,fontWeight:600,color:isInvestable?T.green:T.textMuted}}>{fmt(total)}</span></div>}
        {ed&&type===na.type&&<div style={{display:'flex',gap:8,marginTop:8}}>
          <input className="cmd-input" placeholder={`Add ${label.toLowerCase()}`} value={na.name} onChange={e=>setNa(p=>({...p,name:e.target.value}))} style={{flex:1,padding:'8px 12px',fontSize:13}}/>
          <div style={{position:'relative'}}><span style={{position:'absolute',left:8,top:'50%',transform:'translateY(-50%)',color:T.textMuted,fontSize:12}}>$</span><input type="number" className="cmd-input" placeholder="0" value={na.value||''} onChange={e=>setNa(p=>({...p,value:parseFloat(e.target.value)||0}))} style={{width:100,paddingLeft:22,fontSize:13}}/></div>
          <Btn small green onClick={()=>{if(na.name&&na.value>0){onAddAsset(na);setNa({name:'',value:0,type});}}}>Add</Btn>
        </div>}
        {ed&&type!==na.type&&<button onClick={()=>setNa({name:'',value:0,type})} style={{background:'transparent',border:`1px dashed ${T.borderFaint}`,borderRadius:6,color:T.textMuted,fontSize:11,padding:'5px 10px',cursor:'pointer',marginTop:4}}>+ Add {label.toLowerCase()}</button>}
      </div>
    );
}
function WealthTab({S,nw,onUpdAsset,onAddAsset,onDelAsset,onUpdLiab,onAddLiab,onDelLiab,onUpdTarget,onSave}){
  const safeNw=nw||{assets:0,liabs:0,net:0,investable:0,investableNet:0,homeEquity:0,businessEquity:0};
  const [ed,setEd]=useState(false);
  const [na,setNa]=useState({name:'',value:0,type:'invested'});
  const [nl,setNl]=useState({name:'',balance:0,repayment:0});
  // Local buffer for asset/liability values — prevents cursor jumping on keystroke
  // Values flushed to global state on blur, not on every keypress
  const [assetBuf,setAssetBuf]=useState({});
  const [liabBuf,setLiabBuf]=useState({});
  const getAssetVal=(i,field,fallback)=>assetBuf[`${i}_${field}`]??fallback;
  const getLiabVal=(i,field,fallback)=>liabBuf[`${i}_${field}`]??fallback;
  const flushAsset=(i,field,val)=>{onUpdAsset(i,parseFloat(val)||0);setAssetBuf(p=>{const n={...p};delete n[`${i}_${field}`];return n;});};
  const flushLiab=(i,field,val)=>{onUpdLiab(i,field,parseFloat(val)||0);setLiabBuf(p=>{const n={...p};delete n[`${i}_${field}`];return n;});};
  const isFirstVisit=(S.assets?.items||[]).length===0&&!S.targetNW;

  const currYr=new Date().getFullYear();
  const targetYr=parseInt(S.targetYear)||2035;
  const yrsToTarget=Math.max(1,targetYr-currYr);
  const weeklyInv=S.weeklyInvestments||0;
  const annualInv=weeklyInv*52;

  // Progress toward target uses investableNet only
  const investableNet=safeNw.investableNet||0;
  const pct=S.targetNW>0?Math.min(100,(investableNet/S.targetNW)*100):0;
  const passiveIncome=S.targetNW>0?Math.round((S.targetNW*0.05)/12):0;

  // Trajectory using investableNet
  const tr=[0,1,2,3,5,Math.min(8,yrsToTarget),yrsToTarget]
    .filter((v,i,a)=>v>=0&&a.indexOf(v)===i).sort((a,b)=>a-b).slice(0,7)
    .map(y=>{
      const g=Math.pow(1.07,y);
      const p=investableNet*g+(annualInv>0?annualInv*((g-1)/0.07):0);
      return{yr:currYr+y,p:Math.round(p),pct:S.targetNW>0?Math.min(100,(p/S.targetNW)*100):0};
    });
  const onTrack=tr[tr.length-1]?.p>=(S.targetNW||0);

  // Gap advisory
  const projectedAtTarget=tr[tr.length-1]?.p||0;
  const gap=S.targetNW>0?Math.max(0,S.targetNW-projectedAtTarget):0;
  const extraWeeklyNeeded=gap>0?Math.round(gap/((Math.pow(1.07,yrsToTarget)-1)/0.07)/52):0;

  // Advice
  const advice=calcWealthAdvice(S,safeNw);

  // Group assets by type
  const items=S.assets?.items||[];
  const byType={};
  Object.keys(ASSET_TYPE_LABELS).forEach(t=>{byType[t]=items.filter(a=>(a.type||'other')===t);});


  return(
    <div>
      {isFirstVisit&&<ContextTip id="wealth_intro" icon="◆" title="Set your destination — then COMMAND maps the real path" body="Log your assets by type — shares, super, property, cash. Your primary home equity is tracked separately and excluded from your wealth target (you can't invest what you live in). Set a target and year and COMMAND shows exactly what it will take to get there." action="Log my wealth" color={T.blue}/>}

      {/* HEADLINE — investable wealth only */}
      <Card style={{borderColor:T.blueD,marginBottom:14}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:12,marginBottom:12}}>
          <div>
            <Slab>Investable Net Worth</Slab>
            <NUM size={38} color={investableNet>=0?T.blue:T.red}>{fmt(investableNet)}</NUM>
            <div style={{fontSize:11,color:T.textMuted,marginTop:4}}>
              Total assets: <span style={{color:T.green,fontWeight:500}}>{fmt(safeNw.assets)}</span>
              {safeNw.homeEquity>0&&<> · Home equity excluded: <span style={{color:T.textMuted,fontWeight:500}}>{fmt(safeNw.homeEquity)}</span></>}
              {safeNw.businessEquity>0&&<> · Business excluded: <span style={{color:T.textMuted,fontWeight:500}}>{fmt(safeNw.businessEquity)}</span></>}
            </div>
            {safeNw.liabs>0&&<div style={{fontSize:11,color:T.red,marginTop:2}}>Liabilities: {fmt(safeNw.liabs)}</div>}
          </div>
          <div style={{textAlign:'right',flexShrink:0}}>
            {S.targetNW>0&&<><NUM size={24} color={T.blue}>{pct.toFixed(1)}%</NUM><div style={{fontSize:10,color:T.textMuted,marginBottom:8}}>of target</div></>}
            <Btn primary small onClick={()=>{setEd(!ed);if(!ed&&onSave)onSave();}}>{ed?'✓ Done':'Update values'}</Btn>
          </div>
        </div>

        {S.targetNW>0&&<>
          <PBar pct={pct} color={`linear-gradient(90deg,${T.blueD},${T.blue})`} height={8}/>
          <div style={{marginTop:10,padding:'10px 14px',background:T.bgBase,borderRadius:8,border:`1px solid ${T.borderFaint}`,fontSize:12}}>
            <div style={{color:T.textMuted,marginBottom:3}}>Target: <span style={{color:T.blue,fontWeight:600}}>{fmt(S.targetNW)}</span> by <span style={{color:T.blue,fontWeight:600}}>{targetYr}</span> — {yrsToTarget} years away</div>
            <div style={{color:T.textSecond}}>At 5% withdrawal = <span style={{color:T.green,fontWeight:700}}>{fmt(passiveIncome)}/month</span> passive income — financial independence.</div>
          </div>
          {!onTrack&&gap>0&&<div style={{marginTop:8,padding:'10px 14px',background:'rgba(240,192,64,.06)',border:`1px solid ${T.amberD}`,borderRadius:8,fontSize:12,color:T.amber}}>
            ⚡ Trajectory reaches <strong>{fmt(projectedAtTarget)}</strong> by {targetYr} — <strong>{fmt(gap)}</strong> short. Add <strong>{fmt(extraWeeklyNeeded)}/week</strong> to investment sweeps to close the gap.
          </div>}
          {onTrack&&<div style={{marginTop:8,padding:'10px 14px',background:'rgba(61,232,154,.06)',border:`1px solid ${T.greenD}`,borderRadius:8,fontSize:12,color:T.green}}>
            ✓ On track to reach {fmt(S.targetNW)} by {targetYr}. Maintain sweep discipline — do not let lifestyle creep steal the gap.
          </div>}
        </>}
        {!S.targetNW&&ed&&<div style={{marginTop:10,display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
          <div><div style={{fontSize:11,color:T.textMuted,marginBottom:4}}>Investable wealth target ($)</div><input type="number" value={getAssetVal('tnw','v',S.targetNW)||''} onChange={e=>setAssetBuf(p=>({...p,['tnw_v']:e.target.value}))} onBlur={e=>onUpdTarget('targetNW',parseFloat(e.target.value)||0)} className="num-input" style={{width:'100%'}} placeholder="e.g. 2000000"/></div>
          <div><div style={{fontSize:11,color:T.textMuted,marginBottom:4}}>Target year</div><input type="number" value={S.targetYear||''} onChange={e=>onUpdTarget('targetYear',parseInt(e.target.value)||2035)} className="num-input" style={{width:'100%'}} placeholder="e.g. 2035"/></div>
        </div>}
        {!S.targetNW&&!ed&&<div style={{marginTop:10,padding:'10px 14px',background:T.bgBase,borderRadius:8,border:`1px solid ${T.blueD}`,fontSize:13,color:T.textSecond}}>
          Set an investable wealth target to see your real trajectory and gap advisory.
        </div>}
      </Card>

      {/* ADVICE PANEL */}
      {advice.length>0&&<div style={{marginBottom:14}}>
        {advice.map((a,i)=>(
          <div key={i} style={{marginBottom:8,padding:'12px 14px',background:a.type==='warning'?'rgba(240,96,112,.06)':a.type==='opportunity'?'rgba(61,232,154,.06)':a.type==='action'?'rgba(240,192,64,.06)':'rgba(77,200,240,.06)',border:`1px solid ${a.type==='warning'?T.redD:a.type==='opportunity'?T.greenD:a.type==='action'?T.amberD:T.blueD}`,borderRadius:8}}>
            <div style={{fontSize:12,fontWeight:600,color:a.type==='warning'?T.red:a.type==='opportunity'?T.green:a.type==='action'?T.amber:T.blue,marginBottom:4}}>{a.icon} {a.title}</div>
            <div style={{fontSize:12,color:T.textSecond,lineHeight:1.65}}>{a.body}</div>
          </div>
        ))}
      </div>}

      <Grid2>
        {/* ASSETS BY TYPE */}
        <Card>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
            <Slab style={{margin:0}}>Assets by type</Slab>
            {S.targetNW>0&&ed&&<div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginTop:8,width:'100%'}}>
              <div><div style={{fontSize:10,color:T.textMuted,marginBottom:3}}>Target ($)</div><input type="number" value={S.targetNW||''} onChange={e=>onUpdTarget('targetNW',parseFloat(e.target.value)||0)} className="num-input" style={{width:'100%'}} placeholder="2000000"/></div>
              <div><div style={{fontSize:10,color:T.textMuted,marginBottom:3}}>Target year</div><input type="number" value={S.targetYear||''} onChange={e=>onUpdTarget('targetYear',parseInt(e.target.value)||2035)} className="num-input" style={{width:'100%'}} placeholder="2035"/></div>
            </div>}
          </div>
          {Object.entries(ASSET_TYPE_LABELS).map(([type,label])=>(
            <TypeSection key={type} type={type} label={label} items={byType[type]||[]} ed={ed} na={na} setNa={setNa} getAssetVal={getAssetVal} setAssetBuf={setAssetBuf} flushAsset={flushAsset} onAddAsset={onAddAsset} onDelAsset={onDelAsset}/>
          ))}
          <div style={{borderTop:`1px solid ${T.borderMid}`,marginTop:8,paddingTop:8,display:'flex',justifyContent:'space-between'}}>
            <span style={{fontSize:12,fontWeight:600}}>Total all assets</span>
            <span style={{color:T.green,fontWeight:700}}>{fmt(safeNw.assets)}</span>
          </div>
        </Card>

        <div>
          {/* LIABILITIES */}
          <Card>
            <Slab>Liabilities</Slab>
            {(S.liabilities?.items||[]).length===0&&!ed&&<div style={{fontSize:12,color:T.textMuted,padding:'6px 0',lineHeight:1.7}}>Add your mortgage, loans, and other debts. COMMAND auto-reduces balances as you make repayments.</div>}
            {(S.liabilities?.items||[]).map((l,i)=>(
              <div key={l.id||i} style={{marginBottom:10,paddingBottom:10,borderBottom:`1px solid ${T.borderFaint}`}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                  <span style={{fontSize:13,fontWeight:500,color:T.textSecond}}>{l.name}</span>
                  {ed&&<button onClick={()=>onDelLiab(i)} style={{background:'transparent',border:'none',color:T.textMuted,cursor:'pointer',fontSize:14}}>×</button>}
                </div>
                {ed?<div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                  <div><label style={{fontSize:10,color:T.textMuted,display:'block',marginBottom:3}}>Current balance</label><input type="number" value={getLiabVal(i,'bal',l.balance)||''} onChange={e=>setLiabBuf(p=>({...p,[`${i}_bal`]:e.target.value}))} onBlur={e=>flushLiab(i,'balance',e.target.value)} className="num-input" style={{width:'100%'}}/></div>
                  <div><label style={{fontSize:10,color:T.textMuted,display:'block',marginBottom:3}}>Monthly repayment</label><input type="number" value={getLiabVal(i,'rep',l.repayment)||''} onChange={e=>setLiabBuf(p=>({...p,[`${i}_rep`]:e.target.value}))} onBlur={e=>flushLiab(i,'repayment',e.target.value)} className="num-input" style={{width:'100%'}}/></div>
                </div>
                :<div style={{fontSize:11,color:T.textMuted}}>{fmt(l.balance)} owing · {fmt(l.repayment)}/mo repayment</div>}
              </div>
            ))}
            {ed&&<><div style={{display:'flex',gap:8,marginTop:8,flexWrap:'wrap'}}>
              <input className="cmd-input" placeholder="e.g. Home Mortgage" value={nl.name} onChange={e=>setNl(p=>({...p,name:e.target.value}))} style={{flex:'1 1 140px',padding:'8px 12px',fontSize:13}}/>
              <div style={{position:'relative',flex:'0 0 100px'}}><span style={{position:'absolute',left:8,top:'50%',transform:'translateY(-50%)',color:T.textMuted,fontSize:11}}>Bal $</span><input type="number" className="cmd-input" placeholder="0" value={nl.balance||''} onChange={e=>setNl(p=>({...p,balance:parseFloat(e.target.value)||0}))} style={{paddingLeft:34,fontSize:12}}/></div>
              <div style={{position:'relative',flex:'0 0 100px'}}><span style={{position:'absolute',left:8,top:'50%',transform:'translateY(-50%)',color:T.textMuted,fontSize:11}}>/mo $</span><input type="number" className="cmd-input" placeholder="0" value={nl.repayment||''} onChange={e=>setNl(p=>({...p,repayment:parseFloat(e.target.value)||0}))} style={{paddingLeft:36,fontSize:12}}/></div>
              <Btn small green onClick={()=>{if(nl.name&&nl.balance>0){onAddLiab(nl);setNl({name:'',balance:0,repayment:0});}}}>Add</Btn>
            </div>
            <div style={{marginTop:14,paddingTop:14,borderTop:`1px solid ${T.borderFaint}`}}>
              <div style={{fontSize:11,color:T.blue,marginBottom:8,fontWeight:600}}>WEALTH TARGET (investable assets only)</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                <div><div style={{fontSize:11,color:T.textMuted,marginBottom:4}}>Target ($)</div><input type="number" value={getAssetVal('tnw','v',S.targetNW)||''} onChange={e=>setAssetBuf(p=>({...p,['tnw_v']:e.target.value}))} onBlur={e=>onUpdTarget('targetNW',parseFloat(e.target.value)||0)} className="num-input" style={{width:'100%'}} placeholder="e.g. 2000000"/></div>
                <div><div style={{fontSize:11,color:T.textMuted,marginBottom:4}}>Target year</div><input type="number" value={S.targetYear||''} onChange={e=>onUpdTarget('targetYear',parseInt(e.target.value)||2035)} className="num-input" style={{width:'100%'}} placeholder="e.g. 2035"/></div>
              </div>
            </div></>}
            <div style={{borderTop:`1px solid ${T.borderMid}`,marginTop:8,paddingTop:8,display:'flex',justifyContent:'space-between'}}><span style={{fontSize:12,fontWeight:600}}>Total Liabilities</span><span style={{color:T.red,fontWeight:700}}>{fmt(safeNw.liabs)}</span></div>
          </Card>

          {/* TRAJECTORY */}
          <Card style={{marginTop:14}}>
            <Slab>Trajectory — investable wealth only</Slab>
            {S.targetNW>0?<>
              {tr.map(t=>{const isT=t.yr===targetYr;return<div key={t.yr} style={{marginBottom:10}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                  <span style={{fontSize:12,color:isT?T.blue:T.textMuted,fontWeight:isT?700:400}}>{t.yr}{isT?' ◀ Target':''}</span>
                  <span style={{fontSize:12,color:t.p>=(S.targetNW||0)?T.green:T.textSecond,fontWeight:500}}>{fmt(t.p)} <span style={{fontSize:10,color:T.textMuted}}>({t.pct.toFixed(0)}%)</span></span>
                </div>
                <PBar pct={t.pct} color={isT?(onTrack?T.greenD:T.amberD):t.p>=(S.targetNW||0)?T.greenD:'#1a3050'} height={5}/>
              </div>;})}
              <div style={{marginTop:12,padding:'10px 12px',background:T.bgBase,borderRadius:8,border:`1px solid ${T.borderFaint}`}}>
                <div style={{fontSize:11,fontWeight:600,color:T.textMuted,marginBottom:8}}>WEALTH IMPERATIVES</div>
                {[
                  'Never skip your weekly sweep — compounding is unforgiving of gaps',
                  `Investment rate ${Math.round(((S.weeklyInvestments||0)/Math.max(1,(S.income?.yours||0)+(S.income?.partner||0)))*100)}% of income — target 20%+ minimum`,
                  'Never hold more than 3 months of expenses in cash — the rest earns nothing',
                  'Maximise super concessional contributions before external investing (post-40)',
                  'Every business surplus goes to work immediately — not lifestyle inflation',
                ].map((a,i)=>(<div key={i} style={{display:'flex',gap:8,marginBottom:6}}><span style={{color:T.blueD,flexShrink:0}}>→</span><span style={{fontSize:11,color:T.textSecond,lineHeight:1.5}}>{a}</span></div>))}
              </div>
            </>:<div style={{padding:'20px 0',textAlign:'center'}}>
              <div style={{fontSize:24,marginBottom:12}}>🎯</div>
              <div style={{fontSize:14,fontWeight:600,marginBottom:8}}>Set your investable wealth target</div>
              <div style={{fontSize:12,color:T.textMuted,lineHeight:1.7,marginBottom:16}}>Enter a target and year above to see your real trajectory — based on invested assets only, not your home.</div>
              <Btn primary small onClick={()=>setEd(true)}>Set your target →</Btn>
            </div>}
          </Card>
        </div>
      </Grid2>
    </div>
  );
}

function GoalsTab({S,onAddGoal,onUpdateGoal,onDeleteGoal}){
  const [showForm,setShowForm]=useState(false);
  const [form,setForm]=useState({cat:'wealth',declaration:'',why:'',dailyAction:'',targetDate:'',milestones:[{text:'',done:false}]});
  const [showDetail,setShowDetail]=useState(false);
  const goals=S.goals||[];
  const catInfo=k=>GOAL_CATS.find(c=>c.k===k)||GOAL_CATS[0];
  const save=()=>{
    if(!form.declaration.trim()||!form.dailyAction.trim())return;
    onAddGoal({...form,id:Date.now(),progress:0,completed:false,createdAt:todayStr(),milestones:form.milestones.filter(m=>m.text.trim())});
    setForm({cat:'wealth',declaration:'',why:'',dailyAction:'',targetDate:'',milestones:[{text:'',done:false}]});
    setShowForm(false);setShowDetail(false);
  };
  return(
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
        <div><div style={{fontFamily:"'Syne',sans-serif",fontSize:16,fontWeight:700}}>Goals & Commitments</div><div style={{fontSize:12,color:T.textMuted,marginTop:2}}>Declarations of who you are becoming. Daily actions that make them inevitable.</div></div>
        <Btn primary onClick={()=>setShowForm(!showForm)}>+ New Goal</Btn>
      </div>
      {showForm&&<Card style={{borderColor:T.blueD,marginBottom:16}}>
        <div style={{padding:'10px 12px',background:'rgba(77,200,240,.06)',border:`1px solid ${T.blueD}`,borderRadius:8,marginBottom:14,fontSize:12,color:T.textSecond,lineHeight:1.7}}>
          <strong style={{color:T.blue}}>Write it as a declaration, not a wish.</strong> "I am building a $2M investment portfolio by 2030" — not "I want to save more money." The daily action is the one behaviour you do every day that makes this inevitable.
        </div>
        <Slab>New goal</Slab>
        <div style={{display:'flex',gap:8,marginBottom:14,flexWrap:'wrap'}}>
          {GOAL_CATS.map(c=><button key={c.k} onClick={()=>setForm(p=>({...p,cat:c.k}))} style={{padding:'5px 12px',borderRadius:20,border:`1px solid ${form.cat===c.k?c.color:T.borderFaint}`,background:form.cat===c.k?`${c.color}20`:'transparent',color:form.cat===c.k?c.color:T.textMuted,fontSize:12,cursor:'pointer',fontFamily:"'Inter',sans-serif"}}>{c.ic} {c.l}</button>)}
        </div>
        <div style={{marginBottom:12}}>
          <label style={{fontSize:12,color:T.textMuted,display:'block',marginBottom:6}}>Declaration <span style={{fontStyle:'italic',fontSize:11}}>— present tense, assertive. "I am building..."</span></label>
          <input className="cmd-input" placeholder='"I am building a $2M property portfolio by 2030."' value={form.declaration} onChange={e=>setForm(p=>({...p,declaration:e.target.value}))}/>
        </div>
        <div style={{marginBottom:12}}>
          <label style={{fontSize:12,color:T.textMuted,display:'block',marginBottom:6}}>Daily action <span style={{fontStyle:'italic',fontSize:11}}>— the one behaviour that moves this forward every day</span></label>
          <input className="cmd-input" placeholder="e.g. Review one opportunity and update my pipeline." value={form.dailyAction} onChange={e=>setForm(p=>({...p,dailyAction:e.target.value}))}/>
        </div>
        <button onClick={()=>setShowDetail(!showDetail)} style={{background:'transparent',border:'none',color:T.textMuted,cursor:'pointer',fontSize:12,padding:'0 0 12px 0',textDecoration:'underline'}}>{showDetail?'Hide extra fields':'+ Add why, target date, milestones (optional)'}</button>
        {showDetail&&<>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:12}}>
            <div><label style={{fontSize:12,color:T.textMuted,display:'block',marginBottom:6}}>Why it matters</label><input className="cmd-input" placeholder="The emotional anchor." value={form.why} onChange={e=>setForm(p=>({...p,why:e.target.value}))}/></div>
            <div><label style={{fontSize:12,color:T.textMuted,display:'block',marginBottom:6}}>Target date</label><DatePicker value={form.targetDate} onChange={v=>setForm(p=>({...p,targetDate:v}))} placeholder="Select target date"/></div>
          </div>
          <div style={{marginBottom:12}}>
            <label style={{fontSize:12,color:T.textMuted,display:'block',marginBottom:6}}>Milestones</label>
            {form.milestones.map((m,i)=>(<div key={i} style={{display:'flex',gap:8,marginBottom:6}}>
              <input className="cmd-input" placeholder={`Milestone ${i+1}`} value={m.text} onChange={e=>setForm(p=>({...p,milestones:p.milestones.map((x,j)=>j===i?{...x,text:e.target.value}:x)}))} style={{padding:'7px 12px',fontSize:13}}/>
              <button onClick={()=>setForm(p=>({...p,milestones:p.milestones.filter((_,j)=>j!==i)}))} style={{background:'transparent',border:'none',color:T.textMuted,cursor:'pointer',fontSize:14}}>×</button>
            </div>))}
            <Btn small onClick={()=>setForm(p=>({...p,milestones:[...p.milestones,{text:'',done:false}]}))}>+ Add milestone</Btn>
          </div>
        </>}
        <div style={{display:'flex',gap:8}}>
          <Btn primary onClick={save} disabled={!form.declaration.trim()||!form.dailyAction.trim()}>Save Goal</Btn>
          <Btn onClick={()=>{setShowForm(false);setShowDetail(false);}}>Cancel</Btn>
        </div>
      </Card>}
      {goals.length===0&&!showForm&&<div>
        <ContextTip
          id="goals_intro"
          icon="🎯"
          title="Goals that stay in front of you every day get done"
          body="Set one goal. Write it as a declaration. Add the one daily action that moves it forward. COMMAND surfaces it on your Command tab every morning alongside your P1 tasks — so it's never out of sight."
          action="Set my first goal"
          onAction={()=>setShowForm(true)}
          xp={20}
          color={T.blue}
        />
        <Card style={{textAlign:'center',padding:36}}>
          <div style={{fontSize:40,marginBottom:14}}>🎯</div>
          <div style={{fontSize:16,fontWeight:700,marginBottom:8}}>No goals set yet</div>
          <div style={{fontSize:13,color:T.textMuted,marginBottom:20,lineHeight:1.7}}>Goals are not wish lists. They are commitments — declarations of who you are becoming, with daily actions that make them inevitable.</div>
          <Btn primary onClick={()=>setShowForm(true)}>Set your first goal →</Btn>
          <div style={{fontSize:11,color:T.blue,marginTop:10}}>+20 XP when you set your first goal</div>
        </Card>
      </div>}
      {goals.map(g=>{
        const cat=catInfo(g.cat);
        const cms=g.milestones.filter(m=>m.done).length,tms=g.milestones.length;
        const prog=tms>0?Math.round((cms/tms)*100):g.progress||0;
        return<Card key={g.id} style={{borderColor:`${cat.color}30`,opacity:g.completed?.6:1}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8}}>
            <div style={{flex:1}}>
              <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:8,flexWrap:'wrap'}}>
                <span style={{padding:'3px 10px',borderRadius:20,background:`${cat.color}15`,border:`1px solid ${cat.color}40`,color:cat.color,fontSize:11,fontWeight:500}}>{cat.ic} {cat.l}</span>
                {g.targetDate&&<span style={{fontSize:11,color:T.textMuted}}>Target: {g.targetDate}</span>}
                {g.completed&&<span style={{fontSize:11,color:T.green,fontWeight:600}}>✓ Completed</span>}
              </div>
              <div style={{fontSize:14,fontWeight:600,marginBottom:6,lineHeight:1.5}}>{g.declaration}</div>
              {g.why&&<div style={{fontSize:12,color:T.textMuted,marginBottom:6,fontStyle:'italic'}}>"{g.why}"</div>}
              <div style={{fontSize:12,color:T.blue,marginBottom:8}}>Daily: {g.dailyAction}</div>
            </div>
            <div style={{display:'flex',gap:6,flexShrink:0,marginLeft:12}}>
              <Btn small green={!g.completed} onClick={()=>onUpdateGoal(g.id,{completed:!g.completed})}>{g.completed?'Reopen':'Complete'}</Btn>
              <Btn small danger onClick={()=>onDeleteGoal(g.id)}>×</Btn>
            </div>
          </div>
          {tms>0&&<div style={{marginBottom:8}}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}><span style={{fontSize:11,color:T.textMuted}}>Milestones</span><span style={{fontSize:11,color:cat.color}}>{cms}/{tms}</span></div>
            {g.milestones.map((m,i)=><div key={i} style={{display:'flex',gap:8,alignItems:'center',marginBottom:5,cursor:'pointer'}} onClick={()=>onUpdateGoal(g.id,{milestones:g.milestones.map((x,j)=>j===i?{...x,done:!x.done}:x)})}>
              <div style={{width:16,height:16,borderRadius:4,border:`1.5px solid ${m.done?T.greenD:T.borderFaint}`,background:m.done?'rgba(26,122,80,.2)':'transparent',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>{m.done&&<span style={{fontSize:9,color:T.green}}>✓</span>}</div>
              <span style={{fontSize:12,color:m.done?T.textMuted:T.textPrimary,textDecoration:m.done?'line-through':'none'}}>{m.text}</span>
            </div>)}
          </div>}
          <PBar pct={prog} color={cat.color} height={4}/>
          <div style={{fontSize:11,color:T.textMuted,marginTop:4}}>{prog}% complete</div>
        </Card>;
      })}
    </div>
  );
}

function JournalTab({S,onSave}){
  const entries=S.journal||[];
  const isFirstVisit=entries.length===0;
  const te=entries.find(e=>e.date===todayStr());
  const [ans,setAns]=useState(te?.answers||['','','']);
  const [saved,setSaved]=useState(!!te);
  const [view,setView]=useState('today');
  const streak=useMemo(()=>{let s=0;const today=new Date();for(let i=0;i<60;i++){const d=new Date(today);d.setDate(d.getDate()-i);const ds=d.toISOString().split('T')[0];if(entries.find(e=>e.date===ds))s++;else break;}return s;},[entries]);
  const save=()=>{if(!ans.some(a=>a.trim()))return;onSave({date:todayStr(),answers:[...ans],savedAt:new Date().toISOString()});setSaved(true);};
  return(
    <div>
      {isFirstVisit&&<ContextTip
        id="journal_intro"
        icon="📓"
        title="2 minutes. 3 prompts. One of the highest-leverage habits you can build."
        body="The journal isn't a diary — it's a daily anchor. What am I building toward today? What am I grateful for? What's on my mind? Answering these 3 questions every morning sets direction, builds gratitude, and clears cognitive load before the day starts. A 7-day streak earns 50 XP."
        color={T.green}
      />}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
        <div><div style={{fontFamily:"'Syne',sans-serif",fontSize:16,fontWeight:700}}>Journal</div><div style={{fontSize:12,color:T.textMuted,marginTop:2}}>{streak>0?`🔥 ${streak}-day streak`:entries.length>0?`${entries.length} entries`:'Start your streak today'}</div></div>
        <div style={{display:'flex',gap:8}}>{['today','history'].map(v=><Btn key={v} small onClick={()=>setView(v)} style={{borderColor:view===v?T.blueD:T.borderFaint,color:view===v?T.blue:T.textMuted}}>{v==='today'?'Today':'History'}</Btn>)}</div>
      </div>
      {view==='today'&&<Card>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
          <div style={{fontSize:13,color:T.textMuted}}>{new Date().toLocaleDateString('en-AU',{weekday:'long',day:'numeric',month:'long'})}</div>
          {saved&&<span style={{fontSize:12,color:T.green,fontWeight:500}}>✓ Saved today</span>}
        </div>
        {JOURNAL_PROMPTS.map((p,i)=>(
          <div key={i} style={{marginBottom:16}}>
            <label style={{fontSize:12,color:T.textSecond,display:'block',marginBottom:8,lineHeight:1.5}}>{i+1}. {p}</label>
            <textarea value={ans[i]} onChange={e=>setAns(p=>{const n=[...p];n[i]=e.target.value;return n;})} placeholder={i===2?'Write freely...':i===0?'What specific action today...':'One thing...'} className="cmd-input" style={{resize:'vertical',minHeight:56,fontFamily:"'Inter',sans-serif",lineHeight:1.6}} rows={2}/>
          </div>
        ))}
        <div style={{display:'flex',gap:10,alignItems:'center'}}>
          <Btn primary onClick={save} disabled={saved}>{saved?'✓ Saved':'Save entry'}</Btn>
          <span style={{fontSize:12,color:T.textMuted}}>All prompts optional.</span>
        </div>
      </Card>}
      {view==='history'&&<div>
        {entries.length===0&&<Card style={{textAlign:'center',padding:28}}><div style={{fontSize:32,marginBottom:8}}>📓</div><div style={{fontSize:14,color:T.textMuted}}>No entries yet.</div></Card>}
        {[...entries].reverse().map(e=>(
          <Card key={e.date}>
            <div style={{fontSize:12,color:T.textMuted,marginBottom:12,fontWeight:500}}>{new Date(e.date+'T12:00:00').toLocaleDateString('en-AU',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}</div>
            {JOURNAL_PROMPTS.map((p,i)=>e.answers[i]?.trim()&&<div key={i} style={{marginBottom:10}}><div style={{fontSize:11,color:T.textMuted,marginBottom:4}}>{p}</div><div style={{fontSize:13,color:T.textSecond,lineHeight:1.6,paddingLeft:10,borderLeft:`2px solid ${T.borderMid}`}}>{e.answers[i]}</div></div>)}
          </Card>
        ))}
      </div>}
    </div>
  );
}

function HabitsTab({S,onToggle,onUpdateHabits}){
  const ti=todayIdx();
  const activeHabits=getActiveHabits(S);
  const maxPts=activeHabits.reduce((t,h)=>t+h.pts*7,0)||84;
  const hs=activeHabits.reduce((t,h)=>t+(S.habits[h.k]||[0,0,0,0,0,0,0]).reduce((s,v)=>s+v*h.pts,0),0);
  const sc=hs>=(maxPts*.7)?T.green:hs>=(maxPts*.4)?T.amber:T.red;
  const [showPicker,setShowPicker]=useState(false);
  const [newHabit,setNewHabit]=useState({l:'',pts:1});
  const selected=S.selectedHabits||DEFAULT_HABITS.slice(0,6).map(h=>h.k);

  const toggleHabitSelection=k=>{
    const cur=selected.includes(k)?selected.filter(x=>x!==k):[...selected,k];
    onUpdateHabits({selectedHabits:cur});
  };
  const addCustom=()=>{
    if(!newHabit.l.trim())return;
    const k='custom_'+Date.now();
    const custom=[...(S.customHabits||[]),{k,l:newHabit.l.trim(),pts:newHabit.pts,ic:'⭐'}];
    const sel=[...selected,k];
    onUpdateHabits({customHabits:custom,selectedHabits:sel,habits:{...S.habits,[k]:[0,0,0,0,0,0,0]}});
    setNewHabit({l:'',pts:1});
  };
  const removeCustom=k=>{
    const custom=(S.customHabits||[]).filter(h=>h.k!==k);
    const sel=selected.filter(x=>x!==k);
    onUpdateHabits({customHabits:custom,selectedHabits:sel});
  };

  const todayDone=activeHabits.filter(h=>(S.habits[h.k]||[0,0,0,0,0,0,0])[ti]>0).length;
  const todayPct=activeHabits.length>0?Math.round((todayDone/activeHabits.length)*100):0;
  const streakMsg=todayPct===100?'All habits done today — elite day.':todayPct>=60?`${activeHabits.length-todayDone} habit${activeHabits.length-todayDone!==1?'s':''} left today — close it out.`:todayPct>0?`${todayDone} of ${activeHabits.length} done today — keep going.`:'No habits logged today yet. Start here.';
  return(
    <div>
      {/* TODAY'S HABITS — fast tap panel */}
      <Card style={{marginBottom:14,borderColor:todayPct===100?T.greenD:todayPct>0?T.blueD:T.borderFaint}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
          <div>
            <Slab style={{margin:0,marginBottom:4}}>Today's habits</Slab>
            <div style={{fontSize:13,color:todayPct===100?T.green:todayPct>=60?T.blue:T.amber,fontWeight:500}}>{streakMsg}</div>
          </div>
          <div style={{textAlign:'right'}}>
            <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:28,color:todayPct===100?T.green:todayPct>0?T.blue:T.textMuted,lineHeight:1}}>{todayPct}%</div>
            <div style={{fontSize:10,color:T.textMuted,marginTop:2}}>today</div>
          </div>
        </div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          {activeHabits.map(h=>{
            const done=(S.habits[h.k]||[0,0,0,0,0,0,0])[ti]>0;
            return<button key={h.k} onClick={()=>onToggle(h.k,ti)} style={{display:'flex',gap:7,alignItems:'center',padding:'8px 14px',borderRadius:20,border:`1.5px solid ${done?T.greenD:T.borderMid}`,background:done?'rgba(26,122,80,.15)':'transparent',cursor:'pointer',fontFamily:"'Inter',sans-serif",fontSize:13,color:done?T.green:T.textSecond,transition:'all .12s'}}>
              <span>{h.ic||'⭐'}</span>
              <span>{h.l}</span>
              {done&&<span style={{fontSize:11,opacity:.8}}>✓</span>}
            </button>;
          })}
        </div>
      </Card>

      <Card style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:12,marginBottom:14}}>
        <div><Slab>Week score</Slab><div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:48,color:sc,lineHeight:1}}>{hs}<span style={{fontSize:20,color:T.textMuted,fontWeight:400}}>/{maxPts}</span></div></div>
        <div style={{flex:1,minWidth:200,maxWidth:400}}>
          <PBar pct={(hs/maxPts)*100} color={sc} height={10}/>
          <div style={{display:'flex',justifyContent:'space-between',fontSize:11,color:T.textMuted,marginTop:6}}><span>Rebuild</span><span>Target 70%</span><span>Elite</span></div>
          <div style={{fontSize:12,marginTop:8,color:hs===0?T.textMuted:hs<maxPts*.4?T.red:hs<maxPts*.7?T.amber:T.green}}>{hs===0?'Tap any day to log. Today is highlighted in blue.':hs<maxPts*.4?'Focus on your foundational habits first.':hs<maxPts*.7?'Good momentum — add one more habit to reach your target.':'Strong week. These behaviours compound your wealth.'}</div>
        </div>
        <Btn small onClick={()=>setShowPicker(!showPicker)} style={{alignSelf:'flex-start'}}>{showPicker?'Done':'✏ Edit habits'}</Btn>
      </Card>

      {showPicker&&<Card style={{marginBottom:14,borderColor:T.blueD}}>
        <Slab>Choose your habits</Slab>
        <div style={{padding:'10px 12px',background:'rgba(77,200,240,.06)',border:`1px solid ${T.blueD}`,borderRadius:8,marginBottom:12,fontSize:12,color:T.textSecond,lineHeight:1.65}}>
          <strong style={{color:T.blue}}>Your score is based only on habits you select.</strong> Start with 4–6 that matter most to you. You can add custom habits too. A perfect week is always achievable — the bar moves with your selection.
        </div>
        {['Body','Mind','Finance','Growth'].map(cat=>{
          const catHabits=DEFAULT_HABITS.filter(h=>h.cat===cat);
          return<div key={cat} style={{marginBottom:14}}>
            <div style={{fontSize:11,color:T.textMuted,fontWeight:600,marginBottom:8,letterSpacing:'.08em'}}>{cat.toUpperCase()}</div>
            <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
              {catHabits.map(h=>{
                const on=selected.includes(h.k);
                return<button key={h.k} onClick={()=>toggleHabitSelection(h.k)} style={{padding:'6px 12px',borderRadius:20,border:`1.5px solid ${on?T.blueD:T.borderFaint}`,background:on?'rgba(44,133,176,.15)':'transparent',color:on?T.blue:T.textMuted,fontSize:12,cursor:'pointer',fontFamily:"'Inter',sans-serif",display:'flex',gap:6,alignItems:'center'}}>
                  <span>{h.ic}</span><span>{h.l}</span><span style={{fontSize:10,opacity:.7}}>·{h.pts}p</span>
                </button>;
              })}
            </div>
          </div>;
        })}
        {(S.customHabits||[]).length>0&&<div style={{marginBottom:14}}>
          <div style={{fontSize:11,color:T.textMuted,fontWeight:600,marginBottom:8,letterSpacing:'.08em'}}>YOUR CUSTOM HABITS</div>
          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
            {(S.customHabits||[]).map(h=>{
              const on=selected.includes(h.k);
              return<div key={h.k} style={{display:'flex',gap:4,alignItems:'center'}}>
                <button onClick={()=>toggleHabitSelection(h.k)} style={{padding:'6px 12px',borderRadius:20,border:`1.5px solid ${on?T.blueD:T.borderFaint}`,background:on?'rgba(44,133,176,.15)':'transparent',color:on?T.blue:T.textMuted,fontSize:12,cursor:'pointer',fontFamily:"'Inter',sans-serif"}}>⭐ {h.l} ·{h.pts}p</button>
                <button onClick={()=>removeCustom(h.k)} style={{background:'transparent',border:'none',color:T.textMuted,cursor:'pointer',fontSize:14,padding:'0 4px'}}>×</button>
              </div>;
            })}
          </div>
        </div>}
        <div style={{borderTop:`1px solid ${T.borderFaint}`,paddingTop:14,marginTop:4}}>
          <div style={{fontSize:11,color:T.textMuted,fontWeight:600,marginBottom:8,letterSpacing:'.08em'}}>ADD YOUR OWN</div>
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            <input className="cmd-input" placeholder="e.g. Journaling, Cold shower..." value={newHabit.l} onChange={e=>setNewHabit(p=>({...p,l:e.target.value}))} onKeyDown={e=>e.key==='Enter'&&addCustom()} style={{flex:1,padding:'8px 12px',fontSize:13}}/>
            <select value={newHabit.pts} onChange={e=>setNewHabit(p=>({...p,pts:parseInt(e.target.value)}))} className="cmd-input" style={{width:'auto',padding:'8px 12px',fontSize:13}}>
              {[1,2,3].map(p=><option key={p} value={p}>{p}pt</option>)}
            </select>
            <Btn small green onClick={addCustom}>Add</Btn>
          </div>
        </div>
      </Card>}

      <Card style={{overflowX:'auto',marginBottom:14}}>
        <div style={{minWidth:480}}>
          <div style={{display:'grid',gridTemplateColumns:'160px repeat(7,1fr) 44px',gap:6,alignItems:'center',marginBottom:10}}>
            <div style={{fontSize:10,color:T.textMuted,fontFamily:"'DM Mono',monospace"}}>HABIT</div>
            {['M','T','W','T','F','S','S'].map((d,i)=><div key={i} style={{textAlign:'center',fontSize:12,color:i===ti?T.blue:T.textMuted,fontWeight:i===ti?700:400}}>{d}</div>)}
            <div style={{textAlign:'center',fontSize:10,color:T.textMuted}}>WK</div>
          </div>
          {activeHabits.map(h=>{
            const row=(S.habits[h.k]||[0,0,0,0,0,0,0]);
            const wp=row.reduce((s,v)=>s+v*h.pts,0);
            return<div key={h.k} style={{display:'grid',gridTemplateColumns:'160px repeat(7,1fr) 44px',gap:6,alignItems:'center',marginBottom:8}}>
              <div style={{fontSize:12,display:'flex',gap:5,alignItems:'center'}}><span>{h.ic||'⭐'}</span><span>{h.l}</span><span style={{fontSize:10,color:T.textMuted}}>·{h.pts}p</span></div>
              {row.map((v,i)=><div key={i} style={{display:'flex',justifyContent:'center'}}>
                <button onClick={()=>onToggle(h.k,i)} style={{width:30,height:30,borderRadius:6,cursor:'pointer',border:`1.5px solid ${v?T.greenD:i===ti?T.blueD:T.borderFaint}`,background:v?'rgba(26,122,80,.2)':i===ti?'rgba(58,133,176,.08)':'transparent',color:v?T.green:T.textMuted,fontSize:12,display:'flex',alignItems:'center',justifyContent:'center',transition:'all .15s'}}>{v?'✓':''}</button>
              </div>)}
              <div style={{textAlign:'center',fontFamily:"'Syne',sans-serif",fontSize:15,fontWeight:700,color:wp>0?T.blue:T.borderFaint}}>{wp}</div>
            </div>;
          })}
          {activeHabits.length===0&&<div style={{padding:'16px 0',fontSize:13,color:T.textMuted}}>No habits selected. Tap "Edit habits" to choose yours.</div>}
        </div>
      </Card>
      <Card><Slab>Achievements</Slab>
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10}}>
          {ACHIEVEMENTS.map(a=><div key={a.id} title={`${a.d} +${a.xp} XP`} style={{padding:10,borderRadius:8,background:S.achievements.includes(a.id)?'rgba(26,122,80,.1)':'transparent',border:`1px solid ${S.achievements.includes(a.id)?T.greenD:T.borderFaint}`,opacity:S.achievements.includes(a.id)?1:.2,transition:'all .3s'}}>
            <div style={{fontSize:20,marginBottom:4}}>{a.ic}</div>
            <div style={{fontSize:11,fontWeight:600}}>{a.l}</div>
            <div style={{fontSize:10,color:T.textSecond,marginTop:2,lineHeight:1.4}}>{a.d}</div>
            <div style={{fontSize:10,color:T.blueD,marginTop:3,fontWeight:500}}>+{a.xp} XP</div>
          </div>)}
        </div>
      </Card>
    </div>
  );
}

function TasksTab({S,onToggle,onAdd,onDel}){
  const [txt,setTxt]=useState('');const [pri,setPri]=useState(2);const [area,setArea]=useState('General');
  const add=()=>{if(!txt.trim())return;onAdd(txt.trim(),pri,area);setTxt('');};
  const p1=S.tasks.filter(t=>t.pri===1),p1d=p1.filter(t=>t.done).length;
  const p1pct=p1.length>0?Math.round((p1d/p1.length)*100):100;
  const taskMsg=p1.length===0?'No P1 tasks. Add what must happen today.':p1pct===100?'All P1 tasks done — strong day. ✓':p1d===0?`${p1.length} task${p1.length!==1?'s':''} to action today. Start with the hardest one.`:`${p1d} of ${p1.length} done — ${p1.length-p1d} remaining today.`;
  return(
    <div>
      {p1.length>0&&<Card style={{marginBottom:14,borderColor:p1pct===100?T.greenD:p1d>0?T.blueD:T.amberD}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
          <div style={{fontSize:13,color:p1pct===100?T.green:p1d>0?T.blue:T.amber,fontWeight:500}}>{taskMsg}</div>
          <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:22,color:p1pct===100?T.green:T.blue}}>{p1pct}%</div>
        </div>
        <PBar pct={p1pct} color={p1pct===100?T.green:p1d>0?T.blue:T.amber} height={4}/>
      </Card>}
      <ContextTip
        id="tasks_intro"
        icon="▣"
        title="P1 tasks are your non-negotiables for today"
        body="P1 tasks surface on your Command tab every morning. Complete them all and earn 50 XP. P2 is this week, P3 is later. Keep P1 to 3–5 max — too many and nothing is actually priority one."
        color={T.amber}
      />
      <Card>
        <Slab>Add task</Slab>
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          <input type="text" value={txt} onChange={e=>setTxt(e.target.value)} onKeyDown={e=>e.key==='Enter'&&add()} placeholder="New task — press Enter to add" className="cmd-input" style={{flex:1,minWidth:180,padding:'8px 12px',fontSize:13}}/>
          <select value={pri} onChange={e=>setPri(parseInt(e.target.value))} className="cmd-input" style={{width:'auto',padding:'8px 12px',fontSize:13}}>
            <option value={1}>P1 — Today</option><option value={2}>P2 — This Week</option><option value={3}>P3 — Later</option>
          </select>
          <select value={area} onChange={e=>setArea(e.target.value)} className="cmd-input" style={{width:'auto',padding:'8px 12px',fontSize:13}}>
            {['Finance','Business','Personal','Health','General'].map(a=><option key={a}>{a}</option>)}
          </select>
          <Btn primary onClick={add}>Add</Btn>
        </div>
      </Card>
      {[{p:1,l:'Today — P1',c:T.red},{p:2,l:'This week — P2',c:T.amber},{p:3,l:'Later — P3',c:T.textMuted}].map(({p,l,c})=>{
        const activeTasks=S.tasks.filter(t=>t.pri===p&&!t.done);
        if(!activeTasks.length)return null;
        return<Card key={p} style={{marginBottom:14,borderLeft:`3px solid ${c}40`}}>
          <Slab style={{margin:'0 0 10px',color:c}}>{l}</Slab>
          {activeTasks.map(t=><Row key={t.id} style={{borderLeft:p===1?`2px solid ${T.amber}50`:'none'}}>
            <CheckBtn done={false} onClick={()=>onToggle(t.id)}/>
            <span style={{flex:1,fontSize:13,color:T.textPrimary}}>{t.text}</span>
            <Pill>{t.area}</Pill>
            <button onClick={()=>onDel(t.id)} style={{background:'transparent',border:'none',color:T.textMuted,fontSize:16,cursor:'pointer',padding:'0 4px',lineHeight:1}}>×</button>
          </Row>)}
        </Card>;
      })}
      {(()=>{const done=S.tasks.filter(t=>t.done);if(!done.length)return null;
        return<details style={{marginTop:8}}><summary style={{fontSize:12,color:T.textMuted,cursor:'pointer',padding:'8px 0',userSelect:'none'}}>{done.length} completed task{done.length!==1?'s':''} — tap to review</summary>
          <Card style={{marginTop:8,opacity:.7}}>
            {done.map(t=><Row key={t.id} style={{opacity:.6}}>
              <CheckBtn done={true} onClick={()=>onToggle(t.id)}/>
              <span style={{flex:1,fontSize:12,textDecoration:'line-through',color:T.textMuted}}>{t.text}</span>
              <button onClick={()=>onDel(t.id)} style={{background:'transparent',border:'none',color:T.textMuted,fontSize:14,cursor:'pointer',padding:'0 4px'}}>×</button>
            </Row>)}
          </Card>
        </details>;
      })()}
    </div>
  );
}


// ── BUDDY TAB ─────────────────────────────────────────────────────────────────
function BuddyTab({S,user,score,up}){
  const [inviteEmail,setInviteEmail]=useState('');
  const [sending,setSending]=useState(false);
  const [msg,setMsg]=useState('');
  const [buddyData,setBuddyData]=useState(null);
  const [loading,setLoading]=useState(false);
  // Generate a stable referral code from user id
  const referralCode=user?.id?.slice(0,8)||'';
  const referralLink=`https://joincommand.com/app?ref=${referralCode}`;

  // Load buddy data if we have a buddy
  useEffect(()=>{
    if(!S.buddyId)return;
    setLoading(true);
    supabase.from('command_state').select('data').eq('id',S.buddyId).single()
      .then(({data,error})=>{
        if(!error&&data?.data){
          const bd=data.data;
          setBuddyData({
            name:bd.buddyDisplayName||'Your buddy',
            xp:bd.xp||0,
            level:bd.level||1,
            lastScore:bd.lastWeeklyScore||0,
            weeklyScore:bd.currentWeeklyScore||0,
          });
        }
        setLoading(false);
      });
  },[S.buddyId]);

  const sendInvite=async()=>{
    if(!inviteEmail.trim()||!inviteEmail.includes('@')){setMsg('Please enter a valid email address.');return;}
    setSending(true);setMsg('');
    // Store invite request — buddy sees it when they log in
    try{
      const{data:buddyRow}=await supabase.from('command_state').select('id,data').eq('data->>buddyEmail',inviteEmail.toLowerCase()).single();
      if(buddyRow){
        // Found the buddy — link both accounts
        const buddyState={...buddyRow.data,buddyId:user.id,buddyDisplayName:S.buddyDisplayName||user.email.split('@')[0],buddyPending:false};
        await supabase.from('command_state').upsert({id:buddyRow.id,data:buddyState,updated_at:new Date().toISOString()});
        const myState={...S,buddyId:buddyRow.id,buddyEmail:inviteEmail.toLowerCase(),buddyDisplayName:buddyRow.data.buddyDisplayName||inviteEmail.split('@')[0],buddyPending:false};
        up(myState);
        setMsg("✓ Buddy connected! You can now see each other's weekly score.");
      } else {
        // Send pending invite — store on our side
        const myState={...S,buddyEmail:inviteEmail.toLowerCase(),buddyPending:true};
        up(myState);
        setMsg('Invite sent. When your buddy signs up and connects with you, your scores will sync.');
      }
    }catch{setMsg('Something went wrong. Please try again.');}
    setSending(false);
  };

  const removeBuddy=()=>{
    const myState={...S,buddyId:null,buddyEmail:null,buddyPending:false,buddyDisplayName:null};
    up(myState);setBuddyData(null);setMsg('');
  };

  const{c:myLvl}=getLvl(S.xp);
  const scoreColor=sc=>sc>=70?T.green:sc>=40?T.amber:T.red;

  return(
    <div>
      <Card style={{marginBottom:14}}>
        <Slab>Buddy accountability</Slab>
        <div style={{fontSize:13,color:T.textSecond,lineHeight:1.7,marginBottom:16}}>
          Invite one person to be your accountability buddy. You'll see each other's weekly score — nothing else. No financial data is ever shared.
        </div>
        <div style={{padding:'10px 14px',background:'rgba(61,232,154,.06)',border:`1px solid ${T.greenD}`,borderRadius:8,fontSize:12,color:T.textSecond,marginBottom:16,lineHeight:1.6}}>
          🔒 <strong style={{color:T.green}}>Privacy guaranteed.</strong> Your buddy only sees your weekly score (0–100) and XP level. Your income, net worth, bills, and financial data are never visible to anyone else.
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <div style={{width:52,height:52,borderRadius:'50%',border:`2px solid ${T.blueD}`,background:T.bgPanel2,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
            <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:16,color:T.blue}}>{score}</div>
          </div>
          <div>
            <div style={{fontSize:13,fontWeight:600}}>Your week score</div>
            <div style={{fontSize:11,color:T.textMuted}}>Level {myLvl.l} — {myLvl.t} · {S.xp} XP</div>
          </div>
        </div>
      </Card>

      {!S.buddyId&&!S.buddyPending&&<div>
        <ContextTip
          id="buddy_intro"
          icon="👥"
          title="The quiet accountability that actually works"
          body="Invite one person — a partner, colleague, or close friend. You both see each other's weekly score and nothing else. No financial data. No habits detail. Just one number, refreshed weekly. Choose someone whose opinion matters to you."
          color={T.green}
        />
        <Card style={{marginBottom:14}}>
          <Slab>Option 1 — Share your referral link</Slab>
          <div style={{fontSize:12,color:T.textSecond,lineHeight:1.65,marginBottom:12}}>
            Share this link with your buddy. When they sign up, they get <strong style={{color:T.green}}>1 month free</strong> — and so do you when they subscribe.
          </div>
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            <div style={{flex:1,padding:'9px 12px',background:T.bgPanel2,border:`1px solid ${T.borderFaint}`,borderRadius:8,fontSize:12,color:T.blue,fontFamily:'monospace',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{referralLink}</div>
            <Btn small onClick={()=>{navigator.clipboard?.writeText(referralLink);setMsg('✓ Link copied to clipboard!');}}>Copy</Btn>
          </div>
          {msg&&msg.includes('copied')&&<div style={{marginTop:8,fontSize:12,color:T.green}}>{msg}</div>}
        </Card>
        <Card>
          <Slab>Option 2 — Invite by email</Slab>
          <div style={{fontSize:12,color:T.textSecond,lineHeight:1.65,marginBottom:12}}>
            Already on COMMAND? Enter their email to connect instantly. Not on COMMAND yet? We'll send them a personal invite from you — including a free month offer.
          </div>
          <div style={{display:'flex',gap:8}}>
            <input className="cmd-input" type="email" placeholder="buddy@example.com" value={inviteEmail} onChange={e=>setInviteEmail(e.target.value)} onKeyDown={e=>e.key==='Enter'&&sendInvite()} style={{flex:1,padding:'9px 14px',fontSize:13}}/>
            <Btn primary onClick={sendInvite} disabled={sending}>{sending?'Sending...':'Connect'}</Btn>
          </div>
          {msg&&!msg.includes('copied')&&<div style={{marginTop:10,fontSize:13,color:msg.startsWith('✓')?T.green:T.amber,padding:'8px 12px',background:msg.startsWith('✓')?'rgba(61,232,154,.08)':'rgba(240,192,64,.08)',borderRadius:6,border:`1px solid ${msg.startsWith('✓')?T.greenD:T.amberD}`}}>{msg}</div>}
        </Card>
      </div>}

      {S.buddyPending&&!S.buddyId&&<Card style={{borderColor:T.amberD}}>
        <Slab style={{color:T.amber}}>Invite pending</Slab>
        <div style={{fontSize:13,color:T.textSecond,marginBottom:12}}>Invite sent to <strong>{S.buddyEmail}</strong>. Waiting for them to connect.</div>
        <Btn danger small onClick={removeBuddy}>Cancel invite</Btn>
        {msg&&<div style={{marginTop:10,fontSize:13,color:T.amber}}>{msg}</div>}
      </Card>}

      {S.buddyId&&<Card style={{borderColor:T.greenD+'50'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
          <Slab style={{margin:0,color:T.green}}>✓ Buddy connected</Slab>
          <Btn danger small onClick={removeBuddy}>Remove buddy</Btn>
        </div>
        {loading?<div style={{textAlign:'center',padding:'20px 0'}}><Spinner/></div>:
        buddyData?<div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:14}}>
            <div style={{padding:'16px',background:T.bgPanel2,borderRadius:8,textAlign:'center',border:`1px solid ${T.borderFaint}`}}>
              <div style={{fontSize:11,color:T.textMuted,marginBottom:6}}>You</div>
              <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:32,color:scoreColor(score),marginBottom:4}}>{score}</div>
              <div style={{fontSize:11,color:T.textMuted}}>Wk score · Lvl {myLvl.l}</div>
              <div style={{fontSize:11,color:T.blue,marginTop:4}}>{S.xp} XP</div>
            </div>
            <div style={{padding:'16px',background:T.bgPanel2,borderRadius:8,textAlign:'center',border:`1px solid ${T.borderFaint}`}}>
              <div style={{fontSize:11,color:T.textMuted,marginBottom:6}}>{buddyData.name}</div>
              <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:32,color:scoreColor(buddyData.weeklyScore||buddyData.lastScore),marginBottom:4}}>{buddyData.weeklyScore||buddyData.lastScore||0}</div>
              <div style={{fontSize:11,color:T.textMuted}}>Wk score · Lvl {buddyData.level}</div>
              <div style={{fontSize:11,color:T.blue,marginTop:4}}>{buddyData.xp} XP</div>
            </div>
          </div>
          {score>=(buddyData.weeklyScore||0)&&<div style={{padding:'10px 14px',background:'rgba(61,232,154,.06)',border:`1px solid ${T.greenD}`,borderRadius:8,fontSize:13,color:T.green,textAlign:'center'}}>
            You're ahead this week — keep building. 💪
          </div>}
          {score<(buddyData.weeklyScore||0)&&<div style={{padding:'10px 14px',background:'rgba(240,192,64,.08)',border:`1px solid ${T.amberD}`,borderRadius:8,fontSize:13,color:T.amber,textAlign:'center'}}>
            Your buddy is ahead — time to close the gap. 🔥
          </div>}
        </div>:<div style={{fontSize:13,color:T.textMuted}}>Buddy data not available yet. They may need to log in to sync.</div>}
      </Card>}

      <Card style={{borderColor:T.borderFaint}}>
        <Slab>How buddy accountability works</Slab>
        {[
          ['Connect',"Enter your buddy's email. They get linked automatically if they're on COMMAND."],
          ['See scores',"You each see the other's weekly score (0–100) and XP level only."],
          ['Stay honest',"Knowing someone else sees your score makes skipping Monday's update genuinely uncomfortable."],
          ['No pressure','Either of you can disconnect at any time. No notifications, no alerts — just visibility.'],
        ].map(([t,d])=><div key={t} style={{display:'flex',gap:12,marginBottom:12,alignItems:'flex-start'}}>
          <div style={{width:6,height:6,borderRadius:'50%',background:T.blueD,flexShrink:0,marginTop:6}}/>
          <div><div style={{fontSize:13,fontWeight:500,marginBottom:2}}>{t}</div><div style={{fontSize:12,color:T.textMuted,lineHeight:1.5}}>{d}</div></div>
        </div>)}
      </Card>
    </div>
  );
}

// ── GUIDE TAB ─────────────────────────────────────────────────────────────────
const GuideHdg=({children})=><div style={{fontFamily:"'Syne',sans-serif",fontSize:15,fontWeight:700,color:T.blue,marginBottom:10,marginTop:18,paddingBottom:6,borderBottom:`1px solid ${T.borderFaint}`}}>{children}</div>;

function GuideTab({onSetTab}){
  const [section,setSection]=useState('faq');
  const [contactForm,setContactForm]=useState({subject:'',message:''});
  const [sending,setSending]=useState(false);
  const [contactMsg,setContactMsg]=useState('');

  const sendContact=async()=>{
    if(!contactForm.subject.trim()||!contactForm.message.trim()){setContactMsg('Please fill in both fields.');return;}
    setSending(true);setContactMsg('');
    try{
      await supabase.from('support_requests').insert({subject:contactForm.subject,message:contactForm.message,created_at:new Date().toISOString()});
      setContactForm({subject:'',message:''});
      setContactMsg('✓ Message sent. We will respond within one business day.');
    }catch(e){
      // Supabase table may not exist yet - show success anyway and log it
      console.log('Support request:', contactForm.subject, contactForm.message);
      setContactMsg('✓ Message received. We will get back to you within one business day.');
    }
    setSending(false);
  };

  const sections=[
    {id:'faq',ic:'❓',l:'FAQ'},
    {id:'methodology',ic:'◈',l:'Methodology'},
    {id:'usage',ic:'📖',l:'Usage Guide'},
    {id:'roadmap',ic:'🔭',l:'Roadmap'},
    {id:'contact',ic:'✉',l:'Contact'},
  ];

  const P=({children})=><div style={{fontSize:13,color:T.textSecond,lineHeight:1.75,marginBottom:10}}>{children}</div>;
  const Q=({q,a})=>{
    const [open,setOpen]=useState(false);
    return(
      <div style={{borderBottom:`1px solid ${T.borderFaint}`,paddingBottom:12,marginBottom:12}}>
        <button onClick={()=>setOpen(!open)} style={{width:'100%',background:'transparent',border:'none',cursor:'pointer',display:'flex',justifyContent:'space-between',alignItems:'center',padding:'6px 0',textAlign:'left'}}>
          <span style={{fontSize:13,fontWeight:600,color:T.textPrimary,flex:1,paddingRight:12}}>{q}</span>
          <span style={{color:T.blue,fontSize:16,flexShrink:0}}>{open?'−':'+'}</span>
        </button>
        {open&&<div style={{fontSize:13,color:T.textSecond,lineHeight:1.75,marginTop:8,paddingLeft:4}}>{a}</div>}
      </div>
    );
  };

  const content={
    faq:(
      <div>
        <GuideHdg>Getting Started</GuideHdg>
        <Q q="How do I set up COMMAND?" a="Go to the Cashflow tab first. Enter your weekly income, fixed costs (mortgage, rent, living expenses), and investment sweeps. That takes about 3 minutes and unlocks your 8-week projection. Then add any upcoming bills in the Bills tab — they'll land automatically in the right week of your projection."/>
        <Q q="What should I do every day?" a="Open the Command tab. It shows your daily brief — financial score, P1 tasks for today, upcoming bills, and your active habit fast-tap. Check it, act on what it tells you, and close the app. That's the whole daily routine."/>
        <Q q="What should I do every Monday?" a="Update your opening balance in the Cashflow tab. Open your banking app, check your main account balance, and enter it. This keeps your 8-week projection accurate. Also confirm your income has landed and execute your investment sweeps."/>
        <Q q="How is my score calculated?" a="Your weekly score is based on five things: cashflow is set up (15 pts), weekly net is positive (20 pts), P1 tasks completed (20 pts), habits logged (20 pts), and net worth progress toward your target (15 pts). Write a journal entry for +5 pts, and set a goal for another +5 pts. Max is 100."/>
        <Q q="What are P1, P2, P3 tasks?" a="P1 = must do today. P2 = this week. P3 = later. P1 tasks appear on your Command tab every morning. Keep P1 to 3–5 maximum — if everything is priority one, nothing is. Complete all your P1s and earn 15 XP."/>
        <Q q="Can I add my own habits?" a="Yes. In the Habits tab, tap 'Choose habits' to select from the default list or add your own custom habits with a name, icon, and XP value. Your score is calculated only from the habits you've selected — so a perfect week is always achievable no matter how many you choose."/>
        <GuideHdg>Subscription & Account</GuideHdg>
        <Q q="How does the free trial work?" a="You get 7 full days of COMMAND free. No credit card required to start. On day 7, you'll be prompted to subscribe. Your data, XP, habits, and settings are all preserved regardless of whether you continue."/>
        <Q q="How do I cancel my subscription?" a="Go to the Settings tab and tap 'Manage subscription'. This opens the Stripe customer portal where you can cancel, update your card, or view invoices. Access continues until the end of the billing period — no partial refunds."/>
        <Q q="I paid but the app still shows trial — what do I do?" a="It can take up to a minute for the subscription to sync. Sign out and sign back in. If it still shows trial, contact us at info@andcap.com.au with your email address and we will fix it immediately."/>
        <Q q="Is my financial data secure?" a="Yes. Your data is stored in Supabase — encrypted at rest and in transit. We use Stripe for all payments and never store card details. Your financial data is private to your account and never shared or sold."/>
        <GuideHdg>Cashflow & Wealth</GuideHdg>
        <Q q="Why does COMMAND use weekly figures?" a="A week is the right financial unit for most people — income arrives weekly or fortnightly, habits are weekly, and 8 weeks of visibility is enough to see problems before they become crises. All figures entered in any frequency (monthly, fortnightly, annual) are automatically converted to weekly."/>
        <Q q="What's the 8-week projection for?" a="It shows you exactly where your cash balance will be each week for the next 8 weeks, with bills landing in the correct week. Green weeks are healthy. Amber is tight. Red is negative. The goal is to never be surprised by a cash shortfall — you should see it coming 4–6 weeks before it arrives."/>
        <Q q="How do I track my net worth?" a="Go to the Wealth tab and tap 'Update values'. Add your assets (property equity, super, shares, savings) and liabilities (mortgage balance, loans). Set a target net worth and year. COMMAND calculates your trajectory and tells you exactly how much more you need to invest weekly to close any gap."/>
      </div>
    ),
    methodology:(
      <div>
        <P>COMMAND is built on a single premise: <strong style={{color:T.blue}}>most people know what they should do financially — they just don't do it consistently.</strong> The gap between knowing and doing is a system problem, not an intelligence problem.</P>
        <GuideHdg>The COMMAND Framework</GuideHdg>
        <P><strong style={{color:T.textPrimary}}>1. Clarity before action.</strong> You cannot improve what you cannot see. COMMAND starts with your real cash position — not last month's bank statement, but today's balance with the next 8 weeks projected forward. Clarity removes the anxiety of not knowing and replaces it with the ability to act.</P>
        <P><strong style={{color:T.textPrimary}}>2. Direction every morning.</strong> The Command tab exists for one reason: to tell you what to do today. Not a list of possibilities. A directive. Your P1 tasks, your active habits, your daily goal action, and your financial brief — synthesised into one screen you check in under 60 seconds.</P>
        <P><strong style={{color:T.textPrimary}}>3. Habits compound.</strong> Wealth is not built in big moments. It is built by small, consistent behaviours repeated over a long time. Tracking your weekly investment sweep, your exercise habit, your journal entry — these seem insignificant in isolation. Over 5 years they are the difference between financial independence and financial fragility.</P>
        <P><strong style={{color:T.textPrimary}}>4. Accountability closes the loop.</strong> The buddy system, the weekly score, the XP and levels — these are not gamification for its own sake. They are accountability mechanisms. A number shared with one other person changes behaviour more reliably than any other intervention. The weekly score is a mirror, not a game.</P>
        <P><strong style={{color:T.textPrimary}}>5. Goals are declarations, not wishes.</strong> A goal in COMMAND is not "I want to save more money." It is "I am building a $2M investment portfolio by 2030, and every day I review one opportunity and update my pipeline." Present tense. Specific. With a daily action that makes it inevitable.</P>
        <GuideHdg>The Weekly Rhythm</GuideHdg>
        <P>Monday: Update opening balance, confirm income landed, execute investment sweeps, review weekly position.<br/>Tuesday–Friday: Open Command tab, complete P1 tasks, log habits.<br/>Daily: 2-minute journal entry with 3 prompts.</P>
        <P>That is the full operating rhythm. Everything else in COMMAND exists to support it.</P>
        <div style={{padding:'14px 16px',background:'rgba(77,200,240,.06)',border:`1px solid ${T.blueD}`,borderRadius:8,marginTop:8}}>
          <div style={{fontSize:13,fontWeight:600,color:T.blue,marginBottom:6}}>📖 Read the full COMMAND guide</div>
          <div style={{fontSize:12,color:T.textSecond,marginBottom:10,lineHeight:1.6}}>The complete methodology — all five principles, the performance mandates, the operating rhythm, and the philosophy behind the system.</div>
          <a href="/guide" target="_blank" rel="noopener noreferrer" style={{display:'inline-block',padding:'8px 16px',borderRadius:7,background:`linear-gradient(135deg,${T.blueD},${T.blue})`,color:'#fff',fontSize:12,fontWeight:700,textDecoration:'none'}}>Open the COMMAND Method guide →</a>
        </div>
        <GuideHdg>Why XP and Levels?</GuideHdg>
        <P>XP tracks real behaviour — not raw intention. Every XP point represents an action taken. The level system exists because progress needs to be visible to be motivating. You will care more about maintaining a habit streak than breaking it. That is not accidental — it is by design.</P>
      </div>
    ),
    usage:(
      <div>
        <GuideHdg>◈ Command Tab</GuideHdg>
        <P>Your daily brief. Opens to this tab every time. Zone 1 shows your financial score, net position, and a daily directive message. Zone 2 shows your P1 tasks and active habits for fast-tap logging. Zone 3 shows upcoming bills and the 8-week cash summary. Check it. Act on it. Done.</P>
        <GuideHdg>⬡ Cashflow Tab</GuideHdg>
        <P>Set this up first. Enter your opening balance (updated every Monday from your banking app), your income by frequency, your weekly investment sweeps, and all fixed outgoings. COMMAND converts everything to weekly and generates your live net position and 8-week projection.</P>
        <GuideHdg>◎ 8-Week Forward Tab</GuideHdg>
        <P>Your cash projection broken into 8 individual weeks. Each week shows income, outgoings, bills due, and your running balance. Green = healthy. Amber = tight (less than one week's income in reserve). Red = negative. Bills you've added appear in the correct week automatically.</P>
        <GuideHdg>≡ Bills Tab</GuideHdg>
        <P>Add any upcoming payment with a name, amount, and due date. It appears in the 8-week projection in the correct week. Mark bills as paid when done — paid bills earn 5 XP. Urgent bills (due within 7 days) appear on your Command tab with a red alert.</P>
        <GuideHdg>◆ Wealth Tab</GuideHdg>
        <P>Log your assets and liabilities. Set a net worth target and year. COMMAND calculates your trajectory at 7% growth, shows your passive income equivalent at target, and tells you exactly how much more to invest weekly to close any gap. Update quarterly.</P>
        <GuideHdg>🎯 Goals Tab</GuideHdg>
        <P>Set goals as declarations in the present tense. Add one daily action — the behaviour you do every day that makes the goal inevitable. Add milestones. Your active goal's daily action surfaces on the Command tab every morning. Goals earn 20 XP when set, 10 XP per milestone completed.</P>
        <GuideHdg>📓 Journal Tab</GuideHdg>
        <P>3 prompts. 2 minutes. What am I building toward today? What am I grateful for? What is on my mind? A daily journal entry earns 5 XP. A 7-day streak earns a bonus. Journal entries are private and date-stamped. They never leave your account.</P>
        <GuideHdg>○ Habits Tab</GuideHdg>
        <P>Choose the habits that matter to you from the default list or add your own. Log today's habits with a single tap from the Command tab or here. Your weekly score is calculated against only your selected habits — so a perfect week is always achievable. Streaks are tracked and displayed.</P>
        <GuideHdg>▣ Tasks Tab</GuideHdg>
        <P>Add tasks with P1/P2/P3 priority and an area (Finance, Health, Work, Personal). P1 tasks appear on your Command tab. Keep P1 to 3–5 maximum. Completing all P1 tasks earns 15 XP. Tasks carry forward until completed or deleted.</P>
        <GuideHdg>👥 Buddy Tab</GuideHdg>
        <P>Invite one person by email. If they're on COMMAND, you connect instantly. If not, they see the invite when they join. You both see each other's weekly score — nothing else. No financial data, no individual habits. Just one number, once a week.</P>
        <GuideHdg>🎁 Rewards Tab</GuideHdg>
        <P>Your XP balance, level, and achievement badges. Rewards unlock at 200, 500, 1000, and 2500 XP — free months, referral deals, and COMMAND Insider access. Every action earns XP. The level table shows exactly what each level unlocks.</P>
        <GuideHdg>⚙ Settings Tab</GuideHdg>
        <P>Account details, subscription status, and manage or cancel your subscription. Also where to send us a message directly without opening your email app.</P>
      </div>
    ),
    roadmap:(
      <div>
        <P>COMMAND is actively developed. Here is what is coming — in rough priority order.</P>
        <GuideHdg>In Development</GuideHdg>
        {[
          ['Email digest','Weekly summary email every Monday morning — your score, net position, and top 3 priorities for the week. No login required to get the brief.'],
          ['Bill reminders','Push notification or email alert 3 days before a bill is due. Never miss a payment again.'],
          ['Income tracking','Log variable income sources (invoices, dividends, rental income) against forecasts. Track actual vs projected over time.'],
        ].map(([t,d])=>(
          <div key={t} style={{marginBottom:14,padding:'12px 14px',background:T.bgPanel2,borderRadius:8,border:`1px solid ${T.blueD}`}}>
            <div style={{fontSize:13,fontWeight:600,color:T.blue,marginBottom:4}}>🔧 {t}</div>
            <div style={{fontSize:12,color:T.textSecond,lineHeight:1.65}}>{d}</div>
          </div>
        ))}
        <GuideHdg>Planned</GuideHdg>
        {[
          ['Bank feed integration','Connect your bank accounts directly so your opening balance updates automatically. Manual entry stays as an option.'],
          ['Partner / family mode','Share the full dashboard (not just score) with a partner. Joint income, joint targets, shared visibility.'],
          ['Annual review module','A structured year-end review that looks at score trends, net worth movement, goal completion rate, and sets the plan for the year ahead.'],
          ['COMMAND Operator','Multi-user version for advisors and CFOs managing multiple clients or entities through one dashboard.'],
          ['Mobile app (iOS / Android)','Native app with push notifications, widget support, and biometric login. The PWA is excellent — the native app will be better.'],
        ].map(([t,d])=>(
          <div key={t} style={{marginBottom:14,padding:'12px 14px',background:T.bgPanel2,borderRadius:8,border:`1px solid ${T.borderFaint}`}}>
            <div style={{fontSize:13,fontWeight:600,color:T.textPrimary,marginBottom:4}}>📍 {t}</div>
            <div style={{fontSize:12,color:T.textSecond,lineHeight:1.65}}>{d}</div>
          </div>
        ))}
        <div style={{padding:'12px 14px',background:'rgba(61,232,154,.06)',border:`1px solid ${T.greenD}`,borderRadius:8,fontSize:12,color:T.textSecond,lineHeight:1.65}}>
          Have a feature idea? Send it through the Contact tab — we read everything and build what operators actually need.
        </div>
      </div>
    ),
    contact:(
      <div>
        <P>We respond to every message within one business day. Questions, bug reports, feature ideas, and feedback are all welcome.</P>
        <div style={{marginBottom:12}}>
          <label style={{fontSize:11,color:T.textMuted,display:'block',marginBottom:5,textTransform:'uppercase',letterSpacing:'.05em'}}>Subject</label>
          <input className="cmd-input" placeholder="e.g. Question about the wealth tab" value={contactForm.subject} onChange={e=>setContactForm(p=>({...p,subject:e.target.value}))} style={{width:'100%',padding:'9px 12px',fontSize:13}}/>
        </div>
        <div style={{marginBottom:14}}>
          <label style={{fontSize:11,color:T.textMuted,display:'block',marginBottom:5,textTransform:'uppercase',letterSpacing:'.05em'}}>Message</label>
          <textarea className="cmd-input" placeholder="Tell us what's on your mind..." value={contactForm.message} onChange={e=>setContactForm(p=>({...p,message:e.target.value}))} rows={5} style={{width:'100%',padding:'9px 12px',fontSize:13,resize:'vertical',fontFamily:"'DM Sans',sans-serif"}}/>
        </div>
        <Btn primary onClick={sendContact} disabled={sending}>{sending?'Sending...':'Send message →'}</Btn>
        {contactMsg&&<div style={{marginTop:12,fontSize:12,color:contactMsg.startsWith('✓')?T.green:T.amber,padding:'10px 14px',background:contactMsg.startsWith('✓')?'rgba(61,232,154,.08)':'rgba(240,192,64,.08)',borderRadius:8,border:`1px solid ${contactMsg.startsWith('✓')?T.greenD:T.amberD}`,lineHeight:1.6}}>{contactMsg}</div>}
        <div style={{marginTop:20,padding:'12px 14px',background:T.bgPanel2,borderRadius:8,border:`1px solid ${T.borderFaint}`,fontSize:12,color:T.textMuted,lineHeight:1.7}}>
          <div style={{marginBottom:4,fontWeight:600,color:T.textSecond}}>AndCap</div>
          <div>info@andcap.com.au</div>
          <div>andcap.com.au</div>
        </div>
      </div>
    ),
  };

  return(
    <div>
      <div style={{display:'flex',gap:6,marginBottom:14,flexWrap:'wrap'}}>
        {sections.map(s=>(
          <button key={s.id} onClick={()=>setSection(s.id)} style={{padding:'6px 14px',borderRadius:20,border:`1px solid ${section===s.id?T.blue:T.borderFaint}`,background:section===s.id?`${T.blue}18`:'transparent',color:section===s.id?T.blue:T.textMuted,fontSize:12,fontWeight:section===s.id?600:400,cursor:'pointer',fontFamily:"'DM Sans',sans-serif",display:'flex',gap:5,alignItems:'center'}}>
            <span>{s.ic}</span><span>{s.l}</span>
          </button>
        ))}
      </div>
      <Card>
        {content[section]}
        <div style={{borderTop:`1px solid ${T.borderFaint}`,paddingTop:14,marginTop:14,display:'flex',gap:8,flexWrap:'wrap'}}>
          <Btn small onClick={()=>onSetTab('command')}>← Back to Command</Btn>
        </div>
      </Card>
    </div>
  );
}


function StatusBadge({status}){
  const cfg={
    done:{label:'✓ Done',bg:'rgba(61,232,154,.12)',color:T.green,border:T.greenD},
    in_progress:{label:'In progress',bg:'rgba(77,200,240,.08)',color:T.blue,border:T.blueD},
    not_started:{label:'Not started',bg:T.bgPanel2,color:T.textMuted,border:T.borderFaint},
  }[status]||{label:'Not started',bg:T.bgPanel2,color:T.textMuted,border:T.borderFaint};
  return<span style={{fontSize:11,padding:'3px 9px',borderRadius:4,background:cfg.bg,color:cfg.color,border:`1px solid ${cfg.border}`,fontWeight:500,flexShrink:0}}>{cfg.label}</span>;
}

function PlanTab({S,nw,cf,onSave}){
  const safeNw=nw||{assets:0,liabs:0,net:0,investable:0,investableNet:0,homeEquity:0};
  const [section,setSection]=useState('checklist');
  const [expanded,setExpanded]=useState(null);

  // Checklist state stored in S.planChecklist
  const checklist=S.planChecklist||{};
  const getStatus=id=>checklist[id]||'not_started';
  const setStatus=(id,status)=>{
    const n={...S,planChecklist:{...checklist,[id]:status}};
    onSave(n);
  };

  // Auto-sense some items from live data
  const monthlyExpenses=(cf?.fixed||0)*4.33;
  const threeMonthBuffer=monthlyExpenses*3;
  const cashBalance=(S.assets?.items||[]).filter(a=>a.type==='cash').reduce((s,a)=>s+(a.value||0),0)+(S.openingBalance||0);
  const invRate=cf?.income>0?Math.round(((S.weeklyInvestments||0)/cf.income)*100):0;
  const superBalance=(S.assets?.items||[]).filter(a=>a.type==='super').reduce((s,a)=>s+(a.value||0),0);

  const autoStatus=(id)=>{
    if(id==='buffer'&&cashBalance>=threeMonthBuffer&&threeMonthBuffer>0)return 'in_progress';
    if(id==='investment_allocation'&&invRate>=20)return 'in_progress';
    if(id==='super_contributions'&&superBalance>0)return 'in_progress';
    return null;
  };

  const effectiveStatus=(id)=>{
    const manual=getStatus(id);
    if(manual!=='not_started')return manual;
    return autoStatus(id)||'not_started';
  };



  const done=PLAN_CHECKLIST.filter(i=>effectiveStatus(i.id)==='done').length;
  const pct=Math.round((done/PLAN_CHECKLIST.length)*100);

  // Wealth roadmap — generated from live data
  const roadmapSteps=[];
  if(!cf?.cashflowSetup){
    roadmapSteps.push({priority:1,icon:'⬡',title:'Set up your finances',body:'Go to the My Money tab and enter your income, fixed costs, and weekly investment sweeps. Takes 3 minutes. Everything else in COMMAND depends on this.',action:'Set up now →',tab:'cashflow',done:false});
  }
  if(cashBalance<threeMonthBuffer&&threeMonthBuffer>0){
    const gap=threeMonthBuffer-cashBalance;
    const weeksToGo=S.weeklyInvestments>0?Math.ceil(gap/((S.weeklyInvestments||1))): null;
    roadmapSteps.push({priority:2,icon:'🛡',title:'Build your emergency buffer',body:`Your buffer is ${fmt(cashBalance)} against a ${fmt(threeMonthBuffer)} target (3 months of expenses). Gap: ${fmt(gap)}.${weeksToGo?` At your current sweep rate, this takes approximately ${weeksToGo} weeks.`:''}`,action:'Log cash assets →',tab:'wealth',done:false});
  }
  if(invRate<20&&cf?.cashflowSetup){
    const extra=cf?.income>0?Math.round(cf.income*0.2-(S.weeklyInvestments||0)):0;
    roadmapSteps.push({priority:3,icon:'📈',title:'Increase investment sweep to 20% of income',body:`Current investment rate: ${invRate}% of income. Target: 20%+. Increasing your sweep by ${fmt(extra)}/week puts you on the right side of compounding. This single change has more long-term impact than almost anything else.`,action:'Update sweeps →',tab:'cashflow',done:invRate>=20});
  }
  if(safeNw.liabs>0){
    const nonDeductible=S.liabilities?.items?.filter(l=>!l.deductible)?.reduce((s,l)=>s+(l.balance||0),0)||0;
    if(nonDeductible>0){
      roadmapSteps.push({priority:4,icon:'✂',title:'Eliminate non-deductible debt',body:`You have ${fmt(nonDeductible)} in non-deductible liabilities. Every dollar here costs you the interest rate in guaranteed losses. Prioritise by interest rate — highest first.`,action:'Review liabilities →',tab:'wealth',done:false});
    }
  }
  if(superBalance>0&&superBalance<250000&&effectiveStatus('super_review')==='not_started'){
    roadmapSteps.push({priority:5,icon:'🔵',title:'Review your super fund',body:`With ${fmt(superBalance)} in super, you are likely better served by a high-performing industry fund (fees <1%) than a retail fund or SMSF. At this balance, fees are the biggest driver of long-term outcomes.`,action:'Mark reviewed →',tab:null,checklistId:'super_review',done:false});
  }
  if(S.targetNW>0&&safeNw.investableNet<S.targetNW){
    const yrs=Math.max(1,(parseInt(S.targetYear)||2035)-new Date().getFullYear());
    const projAtTarget=safeNw.investableNet*Math.pow(1.07,yrs)+((S.weeklyInvestments||0)*52)*((Math.pow(1.07,yrs)-1)/0.07);
    const gap=S.targetNW-projAtTarget;
    if(gap>0){
      const extraWk=Math.round(gap/((Math.pow(1.07,yrs)-1)/0.07)/52);
      roadmapSteps.push({priority:6,icon:'🎯',title:`Close the gap to ${fmt(S.targetNW)}`,body:`At current trajectory you reach ${fmt(Math.round(projAtTarget))} by ${S.targetYear} — ${fmt(Math.round(gap))} short. Adding ${fmt(extraWk)}/week to investment sweeps closes the gap. Every year you delay costs compounding.`,action:'Update sweeps →',tab:'cashflow',done:false});
    }
  }
  if(roadmapSteps.length===0&&cf?.cashflowSetup){
    roadmapSteps.push({priority:1,icon:'✓',title:'You are on track',body:'Your cashflow is positive, your investment rate is strong, and your buffer is in place. Maintain the discipline — the compounding is working.',done:true});
  }

  const currYr=new Date().getFullYear();
  const targetYr=parseInt(S.targetYear)||2035;
  const yrsLeft=Math.max(0,targetYr-currYr);
  const passiveIncome=S.targetNW>0?Math.round((S.targetNW*0.05)/12):0;
  const invPct=S.targetNW>0?Math.min(100,Math.round(((safeNw.investableNet||0)/S.targetNW)*100)):0;

  return(
    <div>
      {/* Section tabs */}
      <div style={{display:'flex',gap:6,marginBottom:14,flexWrap:'wrap'}}>
        {[{id:'checklist',l:'Financial Foundations',ic:'✓'},{id:'roadmap',l:'Wealth Roadmap',ic:'◉'},{id:'snapshot',l:'FI Snapshot',ic:'◆'}].map(s=>(
          <button key={s.id} onClick={()=>setSection(s.id)} style={{padding:'7px 14px',borderRadius:20,border:`1px solid ${section===s.id?T.blue:T.borderFaint}`,background:section===s.id?`${T.blue}18`:'transparent',color:section===s.id?T.blue:T.textMuted,fontSize:12,fontWeight:section===s.id?600:400,cursor:'pointer',fontFamily:"'DM Sans',sans-serif",display:'flex',gap:5,alignItems:'center'}}>
            <span>{s.ic}</span><span>{s.l}</span>
          </button>
        ))}
      </div>

      {section==='checklist'&&<div>
        <Card style={{marginBottom:14}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
            <div>
              <Slab style={{margin:0,marginBottom:4}}>Financial Foundations</Slab>
              <div style={{fontSize:12,color:T.textSecond}}>10 things every operator must have in place. In the right order.</div>
            </div>
            <div style={{textAlign:'center',flexShrink:0}}>
              <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:28,color:pct===100?T.green:T.blue}}>{pct}%</div>
              <div style={{fontSize:10,color:T.textMuted}}>{done}/{PLAN_CHECKLIST.length}</div>
            </div>
          </div>
          <PBar pct={pct} color={pct===100?T.green:`linear-gradient(90deg,${T.blueD},${T.blue})`} height={6}/>
        </Card>

        {PLAN_CHECKLIST.map((item,idx)=>{
          const status=effectiveStatus(item.id);
          const isOpen=expanded===item.id;
          const auto=autoStatus(item.id);
          return(
            <Card key={item.id} style={{marginBottom:10,borderLeft:`3px solid ${status==='done'?T.greenD:status==='in_progress'?T.blueD:T.borderFaint}`}}>
              <div style={{display:'flex',gap:12,alignItems:'flex-start'}}>
                <div style={{width:28,height:28,borderRadius:'50%',background:status==='done'?'rgba(61,232,154,.15)':T.bgPanel2,border:`1px solid ${status==='done'?T.greenD:T.borderFaint}`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,fontSize:13}}>{status==='done'?'✓':idx+1}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:8,flexWrap:'wrap',marginBottom:6}}>
                    <div style={{display:'flex',gap:8,alignItems:'center'}}>
                      <span style={{fontSize:16}}>{item.ic}</span>
                      <span style={{fontSize:14,fontWeight:600,color:T.textPrimary}}>{item.title}</span>
                    </div>
                    <StatusBadge status={effectiveStatus(item.id)}/>
                  </div>
                  <button onClick={()=>setExpanded(isOpen?null:item.id)} style={{background:'transparent',border:'none',color:T.blue,fontSize:12,cursor:'pointer',padding:0,fontFamily:"'DM Sans',sans-serif",marginBottom:isOpen?10:0}}>{isOpen?'▲ Less':'▼ Why this matters'}</button>
                  {isOpen&&<div style={{background:T.bgPanel2,borderRadius:8,padding:'12px 14px',marginBottom:12}}>
                    <div style={{fontSize:12,color:T.textSecond,lineHeight:1.75,marginBottom:8}}><strong style={{color:T.textPrimary}}>Why:</strong> {item.why}</div>
                    <div style={{fontSize:12,color:T.textSecond,lineHeight:1.75}}><strong style={{color:T.blue}}>Recommendation:</strong> {item.rec}</div>
                    {auto&&<div style={{fontSize:11,color:T.blue,marginTop:8,padding:'6px 10px',background:'rgba(77,200,240,.08)',borderRadius:6}}>✓ Auto-detected from your data — status set to In Progress</div>}
                  </div>}
                  {(isOpen||status!=='not_started')&&<div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                    {['not_started','in_progress','done'].map(s=>(
                      <button key={s} onClick={()=>setStatus(item.id,s)} style={{padding:'5px 12px',borderRadius:6,border:`1px solid ${status===s?T.blue:T.borderFaint}`,background:status===s?`${T.blue}15`:'transparent',color:status===s?T.blue:T.textMuted,fontSize:11,cursor:'pointer',fontFamily:"'DM Sans',sans-serif",fontWeight:status===s?600:400}}>
                        {{not_started:'Not started',in_progress:'In progress',done:'Done ✓'}[s]}
                      </button>
                    ))}
                  </div>}
                </div>
              </div>
            </Card>
          );
        })}
      </div>}

      {section==='roadmap'&&<div>
        <Card style={{marginBottom:14}}>
          <Slab>Your Wealth Roadmap</Slab>
          <div style={{fontSize:12,color:T.textSecond,lineHeight:1.65}}>Built from your actual numbers. The highest-leverage action first. As you complete each step, the next bottleneck surfaces automatically.</div>
        </Card>
        {roadmapSteps.sort((a,b)=>a.priority-b.priority).map((step,i)=>(
          <Card key={i} style={{marginBottom:10,borderLeft:`3px solid ${step.done?T.greenD:T.blueD}`,opacity:step.done?.8:1}}>
            <div style={{display:'flex',gap:12,alignItems:'flex-start'}}>
              <div style={{width:32,height:32,borderRadius:'50%',background:step.done?'rgba(61,232,154,.15)':'rgba(77,200,240,.1)',border:`1px solid ${step.done?T.greenD:T.blueD}`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,fontSize:16}}>{step.icon}</div>
              <div style={{flex:1}}>
                <div style={{fontSize:14,fontWeight:600,color:step.done?T.textMuted:T.textPrimary,marginBottom:6,textDecoration:step.done?'line-through':'none'}}>{step.title}</div>
                <div style={{fontSize:12,color:T.textSecond,lineHeight:1.7,marginBottom:step.action?10:0}}>{step.body}</div>
                {step.action&&!step.done&&<Btn small onClick={()=>step.tab&&onSave({...S,_navigate:step.tab})} style={{borderColor:T.blueD,color:T.blue}}>{step.action}</Btn>}
              </div>
            </div>
          </Card>
        ))}
        {roadmapSteps.length===0&&<Card style={{textAlign:'center',padding:32}}>
          <div style={{fontSize:32,marginBottom:12}}>◉</div>
          <div style={{fontSize:15,fontWeight:600,marginBottom:8}}>Set up your finances first</div>
          <div style={{fontSize:13,color:T.textMuted,lineHeight:1.7}}>Add your income and costs in the My Money tab, then log your assets and liabilities in the Wealth tab. Your personalised roadmap generates automatically.</div>
        </Card>}
      </div>}

      {section==='snapshot'&&<div>
        <Card style={{borderColor:T.blueD,marginBottom:14}}>
          <Slab>Financial Independence Snapshot</Slab>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginTop:12}}>
            <div style={{background:T.bgPanel2,borderRadius:10,padding:16,textAlign:'center'}}>
              <div style={{fontSize:11,color:T.textMuted,letterSpacing:'.1em',textTransform:'uppercase',marginBottom:6}}>Invested Net Worth</div>
              <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:28,color:T.blue,lineHeight:1}}>{fmt(safeNw.investableNet||0)}</div>
              {S.targetNW>0&&<div style={{fontSize:11,color:T.textMuted,marginTop:4}}>{invPct}% of target</div>}
            </div>
            <div style={{background:T.bgPanel2,borderRadius:10,padding:16,textAlign:'center'}}>
              <div style={{fontSize:11,color:T.textMuted,letterSpacing:'.1em',textTransform:'uppercase',marginBottom:6}}>Target</div>
              <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:28,color:S.targetNW>0?T.green:T.textMuted,lineHeight:1}}>{S.targetNW>0?fmt(S.targetNW):'Not set'}</div>
              {S.targetNW>0&&<div style={{fontSize:11,color:T.textMuted,marginTop:4}}>by {targetYr}</div>}
            </div>
            {passiveIncome>0&&<div style={{background:'rgba(61,232,154,.06)',border:`1px solid ${T.greenD}`,borderRadius:10,padding:16,textAlign:'center'}}>
              <div style={{fontSize:11,color:T.textMuted,letterSpacing:'.1em',textTransform:'uppercase',marginBottom:6}}>Passive Income at Target</div>
              <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:28,color:T.green,lineHeight:1}}>{fmt(passiveIncome)}</div>
              <div style={{fontSize:11,color:T.textMuted,marginTop:4}}>per month at 5% withdrawal</div>
            </div>}
            {yrsLeft>0&&<div style={{background:T.bgPanel2,borderRadius:10,padding:16,textAlign:'center'}}>
              <div style={{fontSize:11,color:T.textMuted,letterSpacing:'.1em',textTransform:'uppercase',marginBottom:6}}>Time Remaining</div>
              <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:28,color:T.amber,lineHeight:1}}>{yrsLeft}</div>
              <div style={{fontSize:11,color:T.textMuted,marginTop:4}}>years to target</div>
            </div>}
          </div>
          {S.targetNW>0&&<>
            <div style={{margin:'14px 0 8px'}}>
              <PBar pct={invPct} color={`linear-gradient(90deg,${T.blueD},${T.blue})`} height={8}/>
            </div>
            <div style={{padding:'12px 14px',background:T.bgBase,borderRadius:8,border:`1px solid ${T.borderFaint}`,fontSize:12,color:T.textSecond,lineHeight:1.7}}>
              Monthly investment needed to reach target on time:{' '}
              <strong style={{color:T.blue}}>{fmt(Math.round(((S.weeklyInvestments||0)*52)/12))}/month current</strong>.{' '}
              {(()=>{
                if(yrsLeft<=0)return null;
                const needed=Math.round((S.targetNW-safeNw.investableNet*Math.pow(1.07,yrsLeft))/((Math.pow(1.07,yrsLeft)-1)/0.07)/52);
                if(needed<=(S.weeklyInvestments||0))return<span style={{color:T.green}}>✓ On track at current sweep rate.</span>;
                return<span style={{color:T.amber}}>Need {fmt(needed)}/week to hit target — increase sweep by {fmt(Math.max(0,needed-(S.weeklyInvestments||0)))}/week.</span>;
              })()}
            </div>
          </>}
          {!S.targetNW&&<div style={{marginTop:14,padding:'12px 14px',background:T.bgBase,borderRadius:8,border:`1px solid ${T.blueD}`,fontSize:13,color:T.textSecond}}>
            Set a net worth target in the Wealth tab to see your full financial independence snapshot.
          </div>}
        </Card>
      </div>}
    </div>
  );
}


function RewardsTab({S,user,onLogout,onSetTab}){
  const{c:lvl}=getLvl(S.xp);
  const showXPTip=S.xp<100;
  const daysActive=daysSince(S.joinedAt||S.trialStart||new Date().toISOString());
  return(
    <div>
      {showXPTip&&<ContextTip
        id="rewards_intro"
        icon="⭐"
        title="XP isn't decoration — it's a measure of real action"
        body="Every XP point represents a real action. Log habits (+1–3 XP), complete P1 tasks (+15 XP), set up cashflow (+25 XP), write a journal entry (+5 XP). Hit 2,500 XP for a free month. Hit 5,000 XP for the buddy referral deal. Hit 13,000 XP and lock in Sovereign pricing for life. 14 levels — plus infinite Apex tiers beyond. The system never runs out of ceiling."
        color={T.blue}
      />}
      <Card style={{borderColor:T.blueD,marginBottom:16}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:12}}>
          <div><Slab>Your reward status</Slab><div style={{fontFamily:"'Syne',sans-serif",fontSize:28,fontWeight:800,color:T.blue,marginBottom:4}}>Level {lvl.l} — {lvl.t}</div><div style={{fontSize:13,color:T.textSecond}}>{S.xp} XP · {daysActive} days active</div></div>
          <div style={{textAlign:'center'}}><div style={{width:60,height:60,borderRadius:'50%',border:`3px solid ${T.blueD}`,background:T.bgPanel2,display:'flex',alignItems:'center',justifyContent:'center'}}><div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:18,color:T.blue}}>{S.xp}</div></div><div style={{fontSize:10,color:T.textMuted,marginTop:4}}>XP</div></div>
        </div>
      </Card>
      {REWARD_TIERS.map(tier=>(
        <Card key={tier.id} style={{marginBottom:14,borderColor:`${tier.color}30`,opacity:tier.available?1:.7}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:12}}>
            <div>
              <div style={{display:'flex',gap:10,alignItems:'center',marginBottom:4}}><span style={{fontSize:20}}>{tier.ic}</span><span style={{fontFamily:"'Syne',sans-serif",fontSize:15,fontWeight:700,color:tier.color}}>{tier.l}</span>{tier.comingSoon&&<span style={{fontSize:10,padding:'2px 8px',borderRadius:20,background:`${tier.color}15`,border:`1px solid ${tier.color}40`,color:tier.color}}>{tier.comingSoon}</span>}</div>
              <div style={{fontSize:12,color:T.textMuted}}>{tier.desc}</div>
            </div>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {tier.rewards.map((r,i)=>{
              const unlocked=tier.available&&r.available&&(r.xp===0||S.xp>=r.xp);
              return<div key={i} style={{padding:'10px 12px',borderRadius:7,background:unlocked?`${tier.color}08`:T.bgPanel2,border:`1px solid ${unlocked?`${tier.color}30`:T.borderFaint}`,opacity:tier.available&&r.available?1:.5}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:12}}>
                  <div style={{flex:1}}><div style={{fontSize:13,fontWeight:500,marginBottom:3,color:unlocked?T.textPrimary:T.textMuted}}>{r.l}</div><div style={{fontSize:11,color:T.textMuted,lineHeight:1.5}}>{r.d}</div>{r.xp>0&&<div style={{fontSize:10,color:tier.color,marginTop:4}}>{r.xp} XP required {S.xp>=r.xp?'✓':''}</div>}</div>
                  <div style={{flexShrink:0,textAlign:'right'}}>
                    {!tier.available&&<span style={{fontSize:11,color:T.textMuted}}>Coming soon</span>}
                    {tier.available&&r.available&&!unlocked&&<span style={{fontSize:11,color:T.textMuted}}>{r.xp-S.xp} XP to go</span>}
                    {tier.available&&r.available&&unlocked&&r.claim&&<button onClick={async()=>{
  // Notify David via Supabase insert — he sees it in his inbox via Resend
  try{await supabase.from('reward_claims').insert({
    user_email:user?.email,reward:r.l,xp_at_claim:S.xp,
    claimed_at:new Date().toISOString()
  });}catch(e){console.log('Claim log:',e);}
  // Also direct email fallback
  window.location.href=`mailto:info@andcap.com.au?subject=${encodeURIComponent('COMMAND Reward Claim: '+r.l)}&body=${encodeURIComponent('User: '+(user?.email||'unknown')+'\nReward: '+r.l+'\nXP: '+S.xp)}`;
}} style={{fontSize:11,color:tier.color,fontWeight:600,background:'transparent',border:`1px solid ${tier.color}40`,borderRadius:4,padding:'3px 8px',cursor:'pointer',fontFamily:"'DM Sans',sans-serif"}}>Claim →</button>}
                    {tier.available&&r.available&&unlocked&&!r.claim&&<span style={{fontSize:11,color:tier.color,fontWeight:600}}>✓ Active</span>}
                  </div>
                </div>
              </div>;
            })}
          </div>
          {tier.id==='insider'&&S.xp<200&&<div style={{marginTop:10,padding:'8px 12px',background:'rgba(77,200,240,.06)',border:`1px solid ${T.blueD}`,borderRadius:6,fontSize:11,color:T.blue,lineHeight:1.6}}>You're {200-S.xp} XP away from your first insider benefit. Keep building.</div>}
        </Card>
      ))}
      <Card><Slab>How to earn XP</Slab>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
          {[['P1 task complete','15 XP'],['P2/P3 task complete','10 XP'],['Log a habit','1–3 XP'],['Mark a bill paid','5 XP'],['Write a journal entry','5 XP'],['Update net worth','10 XP'],['Set a goal','20 XP'],['Complete a milestone','10 XP'],['Unlock an achievement','10–100 XP'],['Set up cashflow','25 XP (once)']].map(([a,x])=>(
            <div key={a} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'6px 10px',background:T.bgPanel2,borderRadius:6,border:`1px solid ${T.borderFaint}`}}>
              <span style={{fontSize:11,color:T.textSecond}}>{a}</span>
              <span style={{fontSize:11,color:T.blue,fontWeight:600,flexShrink:0,marginLeft:8}}>{x}</span>
            </div>
          ))}
        </div>
        <div style={{marginTop:12,fontSize:11,color:T.textMuted,lineHeight:1.6,padding:'8px 10px',background:T.bgBase,borderRadius:6}}>Rewards are based on sustained engagement — not raw XP. Qualifying conditions protect the value of each reward for the whole community.</div>
      </Card>

      <Card style={{borderColor:T.borderFaint,textAlign:'center',padding:'20px 16px'}}>
        <div style={{fontSize:13,color:T.textMuted,marginBottom:10}}>Account, subscription, and settings</div>
        <Btn primary onClick={()=>onSetTab('settings')}>Go to Settings →</Btn>
      </Card>
    </div>
  );
}

function SettingsTab({S,user,onLogout}){
  const [sending,setSending]=useState(false);
  const [msg,setMsg]=useState('');
  const [form,setForm]=useState({subject:'',body:''});

  const sendMsg=async()=>{
    if(!form.subject.trim()||!form.body.trim()){setMsg('Please fill in both fields.');return;}
    setSending(true);setMsg('');
    try{
      // Use Supabase to log the message — stored as a support request
      await supabase.from('support_requests').insert({
        email:user?.email,
        subject:form.subject,
        message:form.body,
        created_at:new Date().toISOString()
      });
      setForm({subject:'',body:''});
      setMsg('✓ Message sent — we will get back to you within one business day.');
    }catch(e){
      // Fallback — open mailto if Supabase write fails
      console.log('Support request from:', user?.email, form.subject);
      setMsg('✓ Message received. We will get back to you within one business day.');
    }
    setSending(false);
  };

  return(
    <div>
      {/* ACCOUNT */}
      <Card style={{marginBottom:14}}>
        <Slab>Account</Slab>
        <div style={{marginBottom:12}}>
          <div style={{fontSize:11,color:T.textMuted,marginBottom:2,letterSpacing:'.05em',textTransform:'uppercase'}}>Signed in as</div>
          <div style={{fontSize:14,color:T.textSecond,fontWeight:500}}>{user?.email}</div>
        </div>
        <div style={{padding:'12px 14px',background:T.bgPanel2,borderRadius:8,border:`1px solid ${T.borderFaint}`,marginBottom:12}}>
          <div style={{fontSize:12,fontWeight:600,color:S.freeForever?T.green:S.isPaidSubscriber?T.blue:T.amber,marginBottom:4}}>
            {S.freeForever?'✓ Free forever — no subscription needed':S.isPaidSubscriber?'✓ Active subscriber — $10.95 AUD/month':'Trial account'}
          </div>
          <div style={{fontSize:11,color:T.textMuted,lineHeight:1.6}}>
            {S.freeForever?'Your account has been granted permanent free access by AndCap.':
             S.isPaidSubscriber?'Your subscription is active. Use the link below to cancel or update your payment method.':
             'You are currently on a free trial. Subscribe to continue after your trial ends.'}
          </div>
        </div>
        {!S.freeForever&&<div style={{display:'flex',flexDirection:'column',gap:8}}>
          {S.isPaidSubscriber
            ?<a href="https://billing.stripe.com/p/login/8x2eVc79ccBueu26TC4wM00" target="_blank" rel="noopener noreferrer"
                style={{display:'block',padding:'11px 16px',borderRadius:8,border:`1px solid ${T.borderFaint}`,background:'transparent',color:T.textSecond,fontSize:13,textAlign:'center',textDecoration:'none',fontFamily:"'DM Sans',sans-serif"}}>
                Manage subscription → (cancel, update card)
              </a>
            :<a href="https://buy.stripe.com/8x2eVc79ccBueu26TC4wM00?success_url=https%3A%2F%2Fjoincommand.com%2Fapp" target="_blank" rel="noopener noreferrer"
                style={{display:'block',padding:'11px 16px',borderRadius:8,border:'none',background:`linear-gradient(135deg,${T.blueD},${T.blue})`,color:'#fff',fontSize:13,fontWeight:600,textAlign:'center',textDecoration:'none',fontFamily:"'DM Sans',sans-serif"}}>
                Subscribe — $10.95 AUD/month →
              </a>
          }
        </div>}
      </Card>

      {/* CONTACT / SUPPORT */}
      <Card style={{marginBottom:14}}>
        <Slab>Send us a message</Slab>
        <div style={{fontSize:12,color:T.textMuted,marginBottom:14,lineHeight:1.6}}>Questions, feedback, or issues — we read everything and respond within one business day.</div>
        <div style={{marginBottom:10}}>
          <label style={{fontSize:11,color:T.textMuted,display:'block',marginBottom:5,textTransform:'uppercase',letterSpacing:'.05em'}}>Subject</label>
          <input className="cmd-input" placeholder="e.g. Question about cashflow tab" value={form.subject} onChange={e=>setForm(p=>({...p,subject:e.target.value}))} style={{width:'100%',padding:'9px 12px',fontSize:13}}/>
        </div>
        <div style={{marginBottom:12}}>
          <label style={{fontSize:11,color:T.textMuted,display:'block',marginBottom:5,textTransform:'uppercase',letterSpacing:'.05em'}}>Message</label>
          <textarea className="cmd-input" placeholder="Tell us what's on your mind..." value={form.body} onChange={e=>setForm(p=>({...p,body:e.target.value}))} rows={4} style={{width:'100%',padding:'9px 12px',fontSize:13,resize:'vertical',fontFamily:"'DM Sans',sans-serif"}}/>
        </div>
        <Btn primary onClick={sendMsg} disabled={sending}>{sending?'Sending...':'Send message'}</Btn>
        {msg&&<div style={{marginTop:10,fontSize:12,color:msg.startsWith('✓')?T.green:T.amber,padding:'8px 12px',background:msg.startsWith('✓')?'rgba(61,232,154,.08)':'rgba(240,192,64,.08)',borderRadius:6,border:`1px solid ${msg.startsWith('✓')?T.greenD:T.amberD}`}}>{msg}</div>}
      </Card>

      {/* SIGN OUT */}
      <Card>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div>
            <div style={{fontSize:13,fontWeight:600,marginBottom:2}}>Sign out</div>
            <div style={{fontSize:11,color:T.textMuted}}>Your data stays saved and synced.</div>
          </div>
          <button onClick={onLogout} style={{padding:'9px 20px',borderRadius:8,border:`1px solid ${T.borderFaint}`,background:'transparent',color:T.textMuted,cursor:'pointer',fontSize:13,fontFamily:"'DM Sans',sans-serif"}}>Sign out</button>
        </div>
      </Card>
    </div>
  );
}

export default function App(){
  const [screen,setScreen]=useState('loading');
  const [user,setUser]=useState(null);
  const [S,setS]=useState(null);
  const [tab,setTab]=useState('command');
  const [flash,setFlash]=useState(null);
  const [sync,setSync]=useState('loading');


  const loadUser=async u=>{
    setUser(u);
    try{
      const fetchPromise=supabase.from('command_state').select('data').eq('id',u.id).single();
      const timeout=new Promise((_,rej)=>setTimeout(()=>rej(new Error('timeout')),8000));
      const{data,error}=await Promise.race([fetchPromise,timeout]);
      if(error||!data?.data?.onboarded){setScreen('onboarding');setSync('local');return;}
      const def=makeDefault({email:u.email});
      const state={...def,...data.data,email:u.email,income:{...def.income,...(data.data.income||{})},assets:{items:data.data.assets?.items||[]},liabilities:{items:data.data.liabilities?.items||[],lastUpdated:data.data.liabilities?.lastUpdated||todayStr()},goals:data.data.goals||[],journal:data.data.journal||[],achievements:data.data.achievements||[]};
      // Backfill email into Supabase if it wasn't there before
      if(!data.data.email){
        supabase.from('command_state').update({data:{...data.data,email:u.email},updated_at:new Date().toISOString()}).eq('id',u.id).then(()=>console.log('Email backfilled into state'));
      }
      const isFree=FREE_FOREVER.includes(u.email?.toLowerCase())||state.freeForever;
      const isPaid=state.isPaidSubscriber===true;
      
      // PAYWALL CHECK — with webhook fallback
      // If trial expired but not marked as paid, check if they just came from Stripe
      // The success_url redirect means they paid — give them access and let webhook catch up
      const justPaid=window.location.search.includes('success')||
                     document.referrer.includes('stripe.com')||
                     document.referrer.includes('buy.stripe.com');
      
      if(!isFree&&!isPaid&&state.trialStart&&Math.floor((new Date()-new Date(state.trialStart))/86400000)>7){
        if(justPaid){
          // They came from Stripe — webhook may be delayed. Let them in and patch state.
          console.log('Stripe redirect detected — granting access while webhook catches up');
          const patchedState={...state,isPaidSubscriber:true,stripeStatus:'active',subscriptionUpdatedAt:new Date().toISOString()};
          // Write to Supabase immediately as backup in case webhook is slow
          supabase.from('command_state').update({data:patchedState,updated_at:new Date().toISOString()}).eq('id',u.id).then(()=>console.log('Patched subscriber status from redirect'));
          setS(patchedState);setUser(u);setScreen('app');setSync('synced');
          // Clear the URL param
          window.history.replaceState(null,'','/app');
          return;
        }
        setS(state);setUser(u);setScreen('paywall');return;
      }
      setS(state);setScreen('app');setSync('synced');
    }catch(e){
      console.warn('loadUser error:',e?.message||e);
      setScreen('onboarding');
      setSync('local');
    }
  };

  const handleAuth=async(u,extra={})=>{setUser(u);if(extra.isNew){setScreen('onboarding');return;}await loadUser(u);};
  useEffect(()=>{
    const init=async()=>{
      // Handle email confirmation redirect from Supabase
      const hash=window.location.hash;
      if(hash&&hash.includes('access_token')){
        const{data:{session},error}=await supabase.auth.getSession();
        if(session&&!error){
          window.history.replaceState(null,'','/');
          await loadUser(session.user);
          return;
        }
      }
      const{data:{session}}=await supabase.auth.getSession();
      if(!session){setScreen('auth');return;}
      await loadUser(session.user);
    };
    init();
    const{data:{subscription}}=supabase.auth.onAuthStateChange(async(ev,session)=>{
      if(ev==='SIGNED_OUT'){setScreen('auth');setUser(null);setS(null);}
      // SIGNED_IN is handled by handleAuth via the AuthScreen callback — don't double-load
    });
    return()=>subscription.unsubscribe();
  },[]);

  const [shareCard,setShareCard]=useState(null);
  const [overflowOpen,setOverflowOpen]=useState(false);

  const handleLogout=async()=>{await supabase.auth.signOut();setScreen('auth');setUser(null);setS(null);};

  const up=useCallback(async ns=>{
    if(!user)return;setS(ns);
    // Always write email into the data object — required for digest, welcome email, buddy matching
    const nsWithEmail={...ns,email:user.email};
    try{await supabase.from('command_state').upsert({id:user.id,data:nsWithEmail,updated_at:new Date().toISOString()});setSync('synced');}
    catch{setSync('local');}
  },[user]);



  const addXP=useCallback((amt,reason)=>{setFlash({amt,reason});setTimeout(()=>setFlash(null),2400);setS(p=>{
    const x=p.xp+amt;
    const{c}=getLvl(x);
    const n={...p,xp:x,level:c.l};up(n);
    return n;
  });},[up]);
  const unlock=useCallback(id=>{setS(p=>{if(p.achievements.includes(id))return p;const a=ACHIEVEMENTS.find(x=>x.id===id);if(!a)return p;const xp=p.xp+a.xp;const{c}=getLvl(xp);setFlash({amt:a.xp,reason:`${a.ic} ${a.l} unlocked!`});setTimeout(()=>setFlash(null),2400);const n={...p,achievements:[...p.achievements,id],xp,level:c.l};up(n);return n;});},[up]);

  const toggleTask=useCallback(id=>{setS(p=>{
    const task=p.tasks.find(t=>t.id===id);
    const wasAlreadyDone=task?.done;
    const tasks=p.tasks.map(t=>t.id===id?{...t,done:!t.done}:t);
    const n={...p,tasks};up(n);
    // XP only on first completion — tracked via xpEarned flag on the task
    if(task&&!wasAlreadyDone&&!task.xpEarned){
      setTimeout(()=>addXP(task.pri===1?15:10,'Task complete'),0);
      // Mark task as having earned XP so reticking doesn't re-award
      const tasksWithFlag=tasks.map(t=>t.id===id?{...t,xpEarned:true}:t);
      setS(prev=>({...prev,tasks:tasksWithFlag}));
      if(!p.achievements.includes('first_task'))setTimeout(()=>unlock('first_task'),50);
      if(tasksWithFlag.filter(t=>t.pri===1).every(t=>t.done)&&!p.achievements.includes('p1_sweep'))setTimeout(()=>unlock('p1_sweep'),100);
    }
    return n;
  });},[addXP,unlock,up]);
  const addTask=useCallback((text,pri=2,area='General')=>{setS(p=>{const n={...p,tasks:[...p.tasks,{id:p.nextTaskId,text,pri,area,done:false}],nextTaskId:p.nextTaskId+1};up(n);return n;});},[up]);
  const delTask=useCallback(id=>{setS(p=>{const n={...p,tasks:p.tasks.filter(t=>t.id!==id)};up(n);return n;});},[up]);
  const toggleHabit=useCallback((key,di)=>{setS(p=>{
    const alreadyDone=p.habits[key][di]>0;
    const v=alreadyDone?0:1;
    const habits={...p.habits,[key]:p.habits[key].map((x,i)=>i===di?v:x)};
    const n={...p,habits};up(n);
    // XP only awarded when TICKING ON — and only if not already earned today for this habit
    // Track daily XP grants to prevent gaming
    if(v===1){
      const today=todayStr();
      const xpKey=`xp_${key}_${today}`;
      const alreadyEarned=(p.habitXPLog||{})[xpKey];
      if(!alreadyEarned){
        const h=getActiveHabits(p).find(x=>x.k===key)||HABITS.find(x=>x.k===key);
        if(h){
          setTimeout(()=>addXP(h.pts,`${h.l} logged`),0);
          // Log the XP grant so it can't be earned again today
          const habitXPLog={...(p.habitXPLog||{}),[xpKey]:true};
          setS(prev=>({...prev,habitXPLog}));
        }
      }
      const sc=getActiveHabits(p).reduce((t,hd)=>t+(habits[hd.k]||[0,0,0,0,0,0,0]).reduce((s,vv)=>s+vv*hd.pts,0),0);
      if(sc>=50&&!p.achievements.includes('habit_50'))setTimeout(()=>unlock('habit_50'),50);
      if(sc>=84&&!p.achievements.includes('habit_perfect'))setTimeout(()=>unlock('habit_perfect'),100);
      if(key==='exercise'&&habits.exercise.filter(vv=>vv).length>=7&&!p.achievements.includes('streak_7'))setTimeout(()=>unlock('streak_7'),150);
    }
    return n;
  });},[addXP,unlock,up]);
  const toggleBill=useCallback(id=>{setS(p=>{
    const bill=p.bills.find(b=>b.id===id);
    const wasAlreadyPaid=bill?.paid;
    const bills=p.bills.map(b=>b.id===id?{...b,paid:!b.paid}:b);
    const n={...p,bills};up(n);
    // XP only on first payment, tracked via xpEarned flag
    if(bill&&!wasAlreadyPaid&&!bill.xpEarned){
      setTimeout(()=>addXP(5,'Bill paid'),0);
      const billsWithFlag=bills.map(b=>b.id===id?{...b,xpEarned:true}:b);
      setS(prev=>({...prev,bills:billsWithFlag}));
      if(!p.achievements.includes('bill_paid'))setTimeout(()=>unlock('bill_paid'),50);
    }
    return n;
  });},[addXP,unlock,up]);
  const addBill=useCallback(bill=>{setS(p=>{const id=Math.max(0,...(p.bills||[]).map(b=>b.id))+1;const n={...p,bills:[...(p.bills||[]),{id,...bill,paid:false}]};up(n);return n;});},[up]);
  const updInc=useCallback((k,v)=>{setS(p=>{const isFreq=k.endsWith('Freq');const income={...p.income,[k]:isFreq?v:(parseFloat(v)||0)};const n={...p,income};up(n);if(!p.achievements.includes('cashflow_set')&&(income.yours>0||income.partner>0))setTimeout(()=>unlock('cashflow_set'),50);return n;});},[up,unlock]);
  const updOB=useCallback(v=>{setS(p=>{const n={...p,openingBalance:parseFloat(v)||0};up(n);return n;});},[up]);
  const updInv=useCallback((v,isFreq=false)=>{setS(p=>{const n=isFreq?{...p,investmentsFreq:v}:{...p,weeklyInvestments:parseFloat(v)||0};up(n);return n;});},[up]);
  const addFixed=useCallback(f=>{setS(p=>{const n={...p,weeklyFixed:[...(p.weeklyFixed||[]),f]};up(n);return n;});},[up]);
  const delFixed=useCallback(i=>{setS(p=>{const n={...p,weeklyFixed:p.weeklyFixed.filter((_,j)=>j!==i)};up(n);return n;});},[up]);
  const updFixed=useCallback((i,amt,freq)=>{setS(p=>{const wf=p.weeklyFixed.map((x,j)=>j===i?{...x,amount:typeof amt==='number'?amt:x.amount,freq:freq||x.freq||'weekly'}:x);const n={...p,weeklyFixed:wf};up(n);return n;});},[up]);
  const updAsset=useCallback((i,val)=>{setS(p=>{const items=p.assets.items.map((a,j)=>j===i?{...a,value:val}:a);const n={...p,assets:{...p.assets,items}};up(n);return n;});},[up]);
  const addAsset=useCallback(a=>{setS(p=>{const items=[...(p.assets?.items||[]),{id:Date.now(),name:a.name,value:a.value}];const n={...p,assets:{...p.assets,items}};up(n);return n;});},[up]);
  const delAsset=useCallback(i=>{setS(p=>{const items=p.assets.items.filter((_,j)=>j!==i);const n={...p,assets:{...p.assets,items}};up(n);return n;});},[up]);
  const updLiab=useCallback((i,k,val)=>{setS(p=>{const items=p.liabilities.items.map((l,j)=>j===i?{...l,[k]:val}:l);const n={...p,liabilities:{...p.liabilities,items}};up(n);return n;});},[up]);
  const addLiab=useCallback(l=>{setS(p=>{const items=[...(p.liabilities?.items||[]),{id:Date.now(),...l}];const n={...p,liabilities:{...p.liabilities,items,lastUpdated:todayStr()}};up(n);return n;});},[up]);
  const delLiab=useCallback(i=>{setS(p=>{const items=p.liabilities.items.filter((_,j)=>j!==i);const n={...p,liabilities:{...p.liabilities,items}};up(n);return n;});},[up]);
  const updTarget=useCallback((k,v)=>{setS(p=>{const n={...p,[k]:v};up(n);return n;});},[up]);
  const addGoal=useCallback(g=>{setS(p=>{const n={...p,goals:[...(p.goals||[]),g]};up(n);return n;});setTimeout(()=>addXP(20,'Goal created'),0);setTimeout(()=>unlock('first_goal'),50);},[up,addXP,unlock]);
  const updateGoal=useCallback((id,changes)=>{setS(p=>{const goals=p.goals.map(g=>g.id===id?{...g,...changes}:g);const n={...p,goals};up(n);if(changes.milestones){const done=changes.milestones.filter(m=>m.done).length;if(done>0){setTimeout(()=>addXP(10,'Milestone progress'),0);if(done===changes.milestones.length)setTimeout(()=>unlock('goal_milestone'),50);}}return n;});},[up,addXP,unlock]);
  const deleteGoal=useCallback(id=>{setS(p=>{const n={...p,goals:p.goals.filter(g=>g.id!==id)};up(n);return n;});},[up]);
  const updateHabits=useCallback(changes=>{setS(p=>{const n={...p,...changes,habits:{...p.habits,...(changes.habits||{})}};up(n);return n;});},[up]);

  const saveJournal=useCallback(entry=>{setS(p=>{
    const isNewEntry=!(p.journal||[]).find(e=>e.date===entry.date);
    const journal=[...(p.journal||[]).filter(e=>e.date!==entry.date),entry];
    const n={...p,journal};up(n);
    // XP only on first journal entry for a given day — not on re-saves/edits
    if(isNewEntry){
      setTimeout(()=>addXP(5,'Journal entry'),0);
      if(!p.achievements.includes('first_journal'))setTimeout(()=>unlock('first_journal'),50);
      const streak=journal.filter((e,i)=>{const d=new Date();d.setDate(d.getDate()-i);return e.date===d.toISOString().split('T')[0];}).length;
      if(streak>=7&&!p.achievements.includes('journal_7'))setTimeout(()=>unlock('journal_7'),100);
    }
    return n;
  });},[up,addXP,unlock]);

  const cf=useMemo(()=>S?calcCF(S):null,[S?.income,S?.openingBalance,S?.bills,S?.weeklyInvestments,S?.weeklyFixed]);
  const nw=useMemo(()=>S?calcNW(S):null,[S?.assets,S?.liabilities]);
  const score=useMemo(()=>S&&cf&&nw?calcScore(S,cf,nw):0,[S?.tasks,S?.habits,S?.goals,S?.journal,cf?.net,cf?.cashflowSetup,nw?.net]);
  // Store current score in state so buddy can see it
  // Store current score in state so buddy and weekly digest can read it
  useEffect(()=>{if(S&&score!==S.currentWeeklyScore){setS(p=>{const n={...p,currentWeeklyScore:score,currentScore:score};up(n);return n;});}
  },[score]);
  const{c:lvl,n:nxt}=S?getLvl(S.xp):getLvl(0);
  const dateStr=new Date().toLocaleDateString('en-AU',{weekday:'long',day:'numeric',month:'long',year:'numeric'});
  const syncColor=sync==='synced'?T.green:sync==='loading'?T.amber:T.textMuted;
  const trialLeft=S&&S.trialStart&&!S.freeForever?Math.max(0,7-Math.floor((new Date()-new Date(S.trialStart))/86400000)):null;

  if(screen==='loading')return<div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:T.bgBase}}><style>{GS}</style><div style={{textAlign:'center'}}><div style={{fontFamily:"'Syne',sans-serif",fontSize:24,fontWeight:800,color:T.blue,marginBottom:16}}>COMMAND</div><Spinner/></div></div>;
  if(screen==='welcome')return<WelcomeScreen onContinue={()=>setScreen('auth')} onSignIn={()=>setScreen('auth')}/>;
  if(screen==='auth')return<AuthScreen onAuth={handleAuth} showWelcome={()=>setScreen('welcome')} defaultMode='login'/>;
  if(screen==='onboarding')return<Onboarding user={user} onComplete={s=>{setS(s);setScreen('app');setSync('synced');}}/>;
  if(screen==='paywall')return<Paywall onLogout={handleLogout}/>;
  if(!S||!cf||!nw)return<div style={{minHeight:'100vh',background:T.bgBase,display:'flex',alignItems:'center',justifyContent:'center'}}><style>{GS}</style><Spinner/></div>;

  return(
    <div style={{minHeight:'100vh',background:T.bgBase,color:T.textPrimary}} onClick={e=>{if(overflowOpen&&!e.target.closest('.overflow-menu-area'))setOverflowOpen(false);}}>
      <style>{GS}
        {`@media(max-width:640px){
          .desktop-tab-row{display:none!important;}
          .mobile-tab-bar{display:flex!important;}
          .app-content{padding:16px 12px 90px!important;}
          .app-header-meta{display:none!important;}
          .app-header-actions{gap:8px!important;}
          .sign-out-btn{display:none!important;}
          .help-btn{display:none!important;}
        }
        @media(min-width:641px){
          .mobile-tab-bar{display:none!important;}
          .app-content{padding:20px 16px 60px;}
        }`}
      </style>
      {flash&&<div style={{position:'fixed',top:16,right:16,background:T.bgPanel,border:`1px solid ${T.blueD}`,color:T.blue,padding:'8px 14px',borderRadius:8,fontSize:13,fontWeight:500,zIndex:9999,animation:'fadeUp .25s ease',pointerEvents:'none',boxShadow:'0 4px 20px rgba(0,0,0,.4)',maxWidth:260}}><span style={{marginRight:6}}>+{flash.amt} XP</span><span style={{color:T.textSecond,fontSize:12}}>— {flash.reason}</span></div>}

      {/* HEADER */}
      <div style={{padding:'10px 16px 0',borderBottom:`1px solid ${T.borderFaint}`,position:'sticky',top:0,background:T.bgBase,zIndex:100}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',maxWidth:980,margin:'0 auto'}}>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <div style={{fontFamily:"'Syne',sans-serif",fontSize:17,fontWeight:800,color:T.blue,letterSpacing:'.04em',display:'flex',alignItems:'center',gap:8}}>
              COMMAND
              <span title={sync} style={{width:6,height:6,borderRadius:'50%',background:syncColor,display:'inline-block',flexShrink:0,animation:sync==='loading'?'pulse 1.5s infinite':undefined}}/>
            </div>
            <div className="app-header-meta" style={{fontSize:11,color:T.textMuted}}>{dateStr}</div>
          </div>
          <div className="app-header-actions" style={{display:'flex',alignItems:'center',gap:10}}>
            {trialLeft!==null&&<div style={{display:'flex',alignItems:'center',gap:6,flexShrink:0}}>
              {trialLeft>0
                ?<div style={{fontSize:11,color:trialLeft<=3?T.red:T.amber,padding:'3px 10px',background:trialLeft<=3?'rgba(240,96,112,.1)':'rgba(240,192,64,.1)',border:`1px solid ${trialLeft<=3?T.redD:T.amberD}`,borderRadius:6,fontWeight:500}}>
                  {trialLeft}d trial remaining
                </div>
                :<div style={{fontSize:11,color:T.red,padding:'3px 10px',background:'rgba(240,96,112,.1)',border:`1px solid ${T.redD}`,borderRadius:6,fontWeight:600}}>
                  Trial expired
                </div>
              }
            </div>}
            <div className="app-header-meta" style={{textAlign:'right'}}>
              <div style={{fontSize:11,color:T.textMuted}}>Lv.{lvl.l} {lvl.t}</div>
              {nxt&&<div style={{fontSize:10,color:T.textMuted}}>{nxt.xp-S.xp} XP → {nxt.t}</div>}
            </div>
            <div onClick={()=>setTab('rewards')} style={{width:36,height:36,borderRadius:'50%',border:`2px solid ${T.blueD}`,background:T.bgPanel,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,cursor:'pointer'}}>
              <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:11,color:T.blue}}>{S.xp}</div>
            </div>
            <button className="sign-out-btn" onClick={handleLogout} style={{background:'transparent',border:`1px solid ${T.borderFaint}`,color:T.textSecond,cursor:'pointer',fontSize:12,padding:'5px 10px',borderRadius:6,fontFamily:"'DM Sans',sans-serif"}}>Sign Out</button>
            <a className="help-btn" onClick={()=>setTab('guide')} style={{background:'transparent',border:`1px solid ${T.borderFaint}`,color:T.textMuted,cursor:'pointer',fontSize:12,padding:'5px 10px',borderRadius:6,fontFamily:"'DM Sans',sans-serif",textDecoration:'none'}}>Help</a>
          </div>
        </div>
        {/* Desktop tab bar — primary tabs + overflow menu */}
        {(()=>{
          const PRIMARY=['command','cashflow','forward','wealth','habits','tasks'];
          const primaryTabs=TABS.filter(t=>PRIMARY.includes(t.id));
          const secondaryTabs=TABS.filter(t=>!PRIMARY.includes(t.id));
          const hasSecondaryActive=secondaryTabs.some(t=>t.id===tab);
          return(
            <div className="desktop-tab-row" style={{display:'flex',maxWidth:980,margin:'4px auto 0',gap:0,borderBottom:`1px solid ${T.borderFaint}`}}>
              {primaryTabs.map(t=>(
                <button key={t.id} onClick={()=>{setTab(t.id);setOverflowOpen(false);}} className={`tab-btn${tab===t.id?' active':''}`} style={{minWidth:72,flex:'1 1 0'}}>
                  <span className="tab-ic">{t.ic}</span>
                  <span className="tab-label">{t.l}</span>
                </button>
              ))}
              {/* Overflow button */}
              <div style={{position:'relative',flexShrink:0}}>
                <button onClick={()=>setOverflowOpen(o=>!o)} className={`tab-btn${hasSecondaryActive?' active':''}`} style={{minWidth:56,width:56}}>
                  <span className="tab-ic" style={{fontSize:16,letterSpacing:1}}>···</span>
                  <span className="tab-label">{hasSecondaryActive?TABS.find(t=>t.id===tab)?.l||'More':'More'}</span>
                </button>
                {overflowOpen&&<div style={{position:'absolute',top:'100%',right:0,background:T.bgPanel,border:`1px solid ${T.borderMid}`,borderRadius:10,padding:6,zIndex:300,minWidth:160,boxShadow:'0 8px 32px rgba(0,0,0,.5)'}}>
                  {secondaryTabs.map(t=>(
                    <button key={t.id} onClick={()=>{setTab(t.id);setOverflowOpen(false);}} style={{
                      display:'flex',gap:10,alignItems:'center',width:'100%',padding:'9px 12px',
                      background:tab===t.id?`rgba(77,200,240,.08)`:'transparent',
                      border:'none',borderRadius:7,color:tab===t.id?T.blue:T.textSecond,
                      cursor:'pointer',fontSize:13,fontFamily:"'DM Sans',sans-serif",textAlign:'left',
                      transition:'background .1s',
                    }}>
                      <span style={{fontSize:16}}>{t.ic}</span>
                      <span style={{fontWeight:tab===t.id?600:400}}>{t.l}</span>
                    </button>
                  ))}
                </div>}
              </div>
            </div>
          );
        })()}
      </div>

      {/* CONTENT */}
      {shareCard&&<div style={{position:'fixed',bottom:100,left:'50%',transform:'translateX(-50%)',zIndex:500,maxWidth:340,width:'90%'}}>
        <div style={{background:`linear-gradient(135deg,${T.bgPanel},${T.bgPanel2})`,border:`1px solid ${T.blueD}`,borderRadius:16,padding:'20px 24px',boxShadow:'0 20px 60px rgba(0,0,0,.6)',textAlign:'center'}}>
          <div style={{fontFamily:"'Syne',sans-serif",fontSize:12,color:T.blue,letterSpacing:'.1em',textTransform:'uppercase',marginBottom:8}}>COMMAND by AndCap</div>
          <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:28,color:shareCard.type==='perfect'?T.green:T.blue,lineHeight:1,marginBottom:6}}>{shareCard.title}</div>
          <div style={{fontSize:13,color:T.textSecond,marginBottom:16}}>{shareCard.subtitle}</div>
          <div style={{display:'flex',gap:8,justifyContent:'center'}}>
            <button onClick={()=>{
              const text=`${shareCard.title} — ${shareCard.subtitle} | COMMAND by AndCap — joincommand.com`;
              if(navigator.share){navigator.share({title:'COMMAND',text,url:'https://joincommand.com'});}
              else{navigator.clipboard?.writeText(text);setShareCard(p=>({...p,copied:true}));}
            }} style={{padding:'9px 18px',borderRadius:8,border:'none',background:`linear-gradient(135deg,${T.blueD},${T.blue})`,color:'#fff',fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:"'DM Sans',sans-serif"}}>
              {shareCard.copied?'✓ Copied':'Share →'}
            </button>
            <button onClick={()=>setShareCard(null)} style={{padding:'9px 14px',borderRadius:8,border:`1px solid ${T.borderFaint}`,background:'transparent',color:T.textMuted,fontSize:13,cursor:'pointer',fontFamily:"'DM Sans',sans-serif"}}>×</button>
          </div>
        </div>
      </div>}
      <div className="app-content" style={{maxWidth:980,margin:'0 auto'}}>

        {tab==='command'&&<CommandTab S={S} cf={cf} nw={nw} score={score} onToggleTask={toggleTask} onToggleHabit={toggleHabit} onPayBill={toggleBill} onSetTab={setTab}/>}
        {tab==='cashflow'&&<CashflowTab S={S} cf={cf} onInc={updInc} onOB={updOB} onInv={updInv} onAddFixed={addFixed} onDelFixed={delFixed} onUpdFixed={updFixed}/>}
        {tab==='forward'&&<ForwardTab S={S} cf={cf}/>}
        {tab==='bills'&&<BillsTab S={S} onToggleBill={toggleBill} onAddBill={addBill}/>}
        {tab==='wealth'&&<WealthTab S={S} nw={nw} onUpdAsset={updAsset} onAddAsset={addAsset} onDelAsset={delAsset} onUpdLiab={updLiab} onAddLiab={addLiab} onDelLiab={delLiab} onUpdTarget={updTarget} onSave={()=>{setTimeout(()=>addXP(10,'Net worth updated'),0);setTimeout(()=>unlock('wealth_logged'),50);}}/>}
        {tab==='plan'&&<PlanTab S={S} nw={nw} cf={cf} onSave={ns=>{if(ns._navigate){setTab(ns._navigate);const clean={...ns};delete clean._navigate;setS(clean);up(clean);}else{setS(ns);up(ns);}}}/>}
        {tab==='goals'&&<GoalsTab S={S} onAddGoal={addGoal} onUpdateGoal={updateGoal} onDeleteGoal={deleteGoal}/>}
        {tab==='journal'&&<JournalTab S={S} onSave={saveJournal}/>}
        {tab==='habits'&&<HabitsTab S={S} onToggle={toggleHabit} onUpdateHabits={updateHabits}/>}
        {tab==='tasks'&&<TasksTab S={S} onToggle={toggleTask} onAdd={addTask} onDel={delTask}/>}
        {tab==='buddy'&&<BuddyTab S={S} user={user} score={score} up={ns=>up(ns)}/>}
        {tab==='rewards'&&<RewardsTab S={S} user={user} onLogout={handleLogout} onSetTab={setTab}/>}
        {tab==='guide'&&<GuideTab onSetTab={setTab}/>}
        {tab==='settings'&&<SettingsTab S={S} user={user} onLogout={handleLogout}/>}

      </div>

      {/* MOBILE BOTTOM TAB BAR */}
      <div className="mobile-tab-bar" style={{position:'fixed',bottom:0,left:0,right:0,zIndex:200,background:T.bgBase,borderTop:`1px solid ${T.borderFaint}`,display:'none',overflowX:'auto',paddingBottom:'env(safe-area-inset-bottom)'}}>
        {[...TABS,{id:'guide',l:'Guide',ic:'📖'}].map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{flex:'0 0 auto',minWidth:52,padding:'8px 6px 6px',background:'transparent',border:'none',cursor:'pointer',display:'flex',flexDirection:'column',alignItems:'center',gap:2,color:tab===t.id?T.blue:T.textMuted,transition:'color .12s'}}>
            <span style={{fontSize:18,lineHeight:1}}>{t.ic}</span>
            <span style={{fontSize:9,fontWeight:tab===t.id?700:400,fontFamily:"'DM Sans',sans-serif",letterSpacing:'.02em'}}>{t.l}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
